import AppIconButton from "../../../components/form/AppIconButton";
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import type { TFunction } from "i18next";
import { Box, Checkbox, Chip, Collapse, FormControlLabel, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography, CircularProgress, List, ListItemButton, Stack, Tooltip, useTheme } from "@mui/material";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../../../components/dialog";
import { AppButton, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid } from "../../../components/layout";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import Icon from "@mui/material/Icon";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import AddIcon from "@mui/icons-material/Add";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import LibraryAddRoundedIcon from "@mui/icons-material/LibraryAddRounded";
import LibraryAddCheckRoundedIcon from "@mui/icons-material/LibraryAddCheckRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import { useMaterialUIController } from "../../../context";
import { useClientBranding } from "../../../providers/ClientBrandingProvider";
import {
  sendChatMessage,
  fetchSuggestedPrompts,
  predictChatOutputIntent,
  createChatMailDraft,
  subscribeToChatResponse,
  CHAT_RESPONSE_TIMEOUT_ERROR,
  ChatOrchestrationWebSocketPayload,
  type ChatAutoSelection,
  type ChatExecutedQuery,
  type ChatResourceCandidate,
  normalizeChatHistoryMessage,
} from "../../../services/chatService";
import {
  embedRuntimeKey,
  isEmbedPageContentEnabled,
} from "../../../services/embedRuntime";
import {
  embedPageSummaryForSend,
  embedPageUrl,
  grantPageContentConsent,
  hasPageContentConsent,
  looksLikePageIntent,
  requestHostPageText,
  setEmbedPageUrl,
  summarizeHostPage,
} from "../../../services/embedPageContent";
import {
  formatProbabilityPct,
  friendlyIndexLabel,
  mergeResourceMetaFromSelection,
  rankedCandidatesForDisplay,
  type ResourceMeta,
} from "../../../utils/chatResourceLabels";
import { formatProgressLabel, progressDetailFrom, type ChatProgressEntry } from "../utils/chatProgressLabel";
import {
  isOrchestrationErrorPayload,
  resolveChatClientErrorMessage,
} from "../../../services/chatErrorMessage";
import { chatQueryExampleService } from "../../../services/chatQueryExampleService";
import {
  EXAMPLE_SAVE_ROLES,
  asyncWarehouseJobs,
  canSaveAsExample,
  exampleResources,
  exampleTools,
  extractExampleQueries,
  queryToText,
} from "../utils/chatQueryExamples";
import ChatAdaptorPicker, { type ChatAdaptorSelection } from "../../../components/ChatAdaptorPicker";
import ChatIndexPicker from "../../../components/ChatIndexPicker";
import ChatOpenApiPicker, { type OpenApiSelection } from "../../../components/ChatOpenApiPicker";
import ChatSlashCommandPicker from "../../../components/ChatSlashCommandPicker";
import ResponseRenderer from "./ResponseRenderer";
import ChatCopyButton from "./ChatCopyButton";
import FormFillDialog, { type FormFillRow } from "./FormFillDialog";
import { copyableChatText } from "../utils/chatCopyText";
import {
  FormFillBridgeError,
  applyHostFormValues,
  isFormFillEnabled,
  keepKnownSuggestions,
  requestHostFormFields,
  suggestFormFill,
} from "../../../services/embedFormFill";
import type { FormFieldsPayload } from "../../../embed-shared/protocol";
import { useChatHost } from "./chatHost";
import WarehouseJobCard from "./WarehouseJobCard";
import ChatRunChip from "./ChatRunChip";
import ChatToolsPalette from "./ChatToolsPalette";
import ChatProgressSteps from "./ChatProgressSteps";
import ChatScopeBubbles, { type ChatScopeBubbleItem } from "./ChatScopeBubbles";
import {
  chatTurnJobService,
  isTerminalChatTurnStatus,
  parseChatTurnJob,
  type ChatTurnJobView,
} from "../../../services/chatTurnJobService";
import { deleteData, getData } from "../../../api/api";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import { getReportChatShell } from "../../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import userAvatarIcon from "../../../assets/images/icons/user.svg";
import authService from "../../../services/authService";
import { canAccessRoute, getCurrentUserRoles } from "../../../utils/authorization";
import { formatOpenApiMessagePrefix } from "../../../services/openapiCatalogService";
import {
  hasChatMessageScope,
  mergeChatMessageScope,
  stripChatMessagePrefixes,
  type ChatMessageScope,
} from "../../../utils/chatMessageScope";
import {
  type ChatOutputPreference,
  type ChatOutputType,
  type ChatSlashCommand,
  outputPreferenceLabel,
  preferenceToApiBody,
} from "../../../utils/chatSlashCommands";
import { buildChatEmailBodyFromMessage } from "../../../utils/chatResultEmailHtml";
import { recipientDomainNotAllowedText } from "../../../utils/notification";
import PackageLimitHint from "../../../components/PackageLimitHint";
import { requestVpnOtp } from "../../../services/vpnOtpPrompt";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";

const CHAT_SELECTED_INDICES_KEY = "chat-selected-indices";

/** History rail in the narrow embedded panel: overlays the thread instead of squeezing it. */
const compactRailOverlaySx = {
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  zIndex: 7,
} as const;

function readStoredIndices(): string[] {
  try {
    const raw = sessionStorage.getItem(CHAT_SELECTED_INDICES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string" && x.trim()) : [];
  } catch {
    return [];
  }
}

type MessageRole = "user" | "assistant";

interface ChatMessage {
  id: string;
  role: MessageRole;
  /** Visible bubble text only (scope prefixes stripped). */
  content?: string;
  /** User picker / typed prefixes at send time (never includes router auto-picks). */
  committedScope?: ChatMessageScope;
  /** Index / OpenAPI / adaptor chips shown above the user bubble (committed + auto). */
  messageScope?: ChatMessageScope;
  payload?: ChatOrchestrationWebSocketPayload;
  /** Transient router notice; does not update index/MCP pickers. */
  autoSelection?: ChatAutoSelection;
  /** Live orchestration step code from interim `progress` WS frames (e.g. routing, opensearch). */
  progressStep?: string;
  /** Optional tool name accompanying the progress step. */
  progressTool?: string;
  /** What the current step is doing, taken from the tool arguments. */
  progressDetail?: string;
  /** Append-only orchestration steps for this turn. */
  progressTimeline?: { step?: string; tool?: string; detail?: string }[];
  /** Thinking bubble is animating away into the run chip. */
  puffing?: boolean;
  error?: string;
  timestamp: string;
  /** Shown above user bubbles: conversation `username` from history or signed-in user when sending. */
  displayUsername?: string;
  /** From persisted chat-history row (technical delivery status). */
  historyStatus?: ChatHistoryMessage["status"];
  /** Assistant turn time from API (`createdAt`) when available. */
  historyCreatedAt?: string;
  /** Stored `responseType` from API; falls back to `payload.type` when rendering. */
  historyResponseType?: string;
  /** Persisted exchange id, used to attach live progress to a reopened turn. */
  historyMessageId?: string;
}

interface ChatHistoryMessage {
  messageId?: string;
  userMessage?: string;
  orchestrationResponse?: unknown;
  responseType?: string;
  status?: "PENDING" | "COMPLETED" | "ERROR";
  errorMessage?: string;
  createdAt?: string;
}

interface ChatHistoryItem {
  id: string;
  title?: string;
  username?: string;
  correlationId?: string;
  createdDate?: string;
  messages?: ChatHistoryMessage[];
}

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const ensureCorrelationId = (current?: string) => {
  if (current && isUuid(current)) return current;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback UUID v4-like generator
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

function isAwaitingHistoryMessage(m: ChatHistoryMessage): boolean {
  return m.status === "PENDING" && (m.orchestrationResponse == null || m.orchestrationResponse === "");
}

function historyItemIsRunning(item: ChatHistoryItem): boolean {
  return (item.messages || []).some(isAwaitingHistoryMessage);
}

function isAwaitingAssistant(m: ChatMessage): boolean {
  return m.role === "assistant" && !m.payload && !m.error;
}

function findAwaitingIndex(messages: ChatMessage[], messageId?: string): number {
  if (messageId) {
    const exact = messages.findIndex(
      (m) =>
        isAwaitingAssistant(m) &&
        (m.historyMessageId === messageId || m.id === `h-assistant-${messageId}`)
    );
    if (exact >= 0) return exact;
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isAwaitingAssistant(messages[i])) return i;
  }
  return -1;
}

function mergeProgressTimeline(
  current: ChatMessage["progressTimeline"],
  step?: string,
  tool?: string,
  detail?: string,
  timeline?: ChatMessage["progressTimeline"]
): ChatMessage["progressTimeline"] {
  if (timeline?.length) return timeline;
  if (!step) return current;
  const last = current?.[current.length - 1];
  if (last?.step === step && (last?.tool || "") === (tool || "")) {
    if ((detail || "") === (last.detail || "")) return current;
    const next = [...(current || [])];
    next[next.length - 1] = { ...last, detail };
    return next;
  }
  return [...(current || []), { step, tool, detail }];
}

function applyProgressToMessages(
  messages: ChatMessage[],
  progress: {
    step?: string;
    tool?: string;
    detail?: string;
    timeline?: ChatMessage["progressTimeline"];
    messageId?: string;
  }
): ChatMessage[] {
  const idx = findAwaitingIndex(messages, progress.messageId);
  if (idx < 0) return messages;
  const current = messages[idx];
  const next = [...messages];
  next[idx] = {
    ...current,
    progressStep: progress.step || current.progressStep,
    progressTool: progress.tool ?? current.progressTool,
    progressDetail: progress.step ? progress.detail : current.progressDetail,
    progressTimeline: mergeProgressTimeline(
      current.progressTimeline,
      progress.step,
      progress.tool,
      progress.detail,
      progress.timeline
    ),
  };
  return next;
}

function readProgressFrame(result: Record<string, unknown> | undefined, message?: string) {
  const step =
    (typeof result?.step === "string" && result.step.trim()) ||
    (typeof message === "string" && message.trim()) ||
    undefined;
  const tool = typeof result?.tool === "string" && result.tool.trim() ? result.tool.trim() : undefined;
  const timeline = Array.isArray(result?.timeline) ? (result.timeline as ChatProgressEntry[]) : undefined;
  return { step, tool, detail: progressDetailFrom(result), timeline };
}

function upsertAssistantTurn(
  messages: ChatMessage[],
  messageId: string | undefined,
  patch: Partial<ChatMessage>
): ChatMessage[] {
  if (messageId) {
    const existing = messages.findIndex(
      (m) =>
        m.role === "assistant" &&
        (m.historyMessageId === messageId || m.id === `h-assistant-${messageId}`)
    );
    if (existing >= 0) {
      const next = [...messages];
      next[existing] = { ...next[existing], ...patch, puffing: false };
      return next;
    }
  }
  const awaiting = findAwaitingIndex(messages, messageId);
  if (awaiting >= 0) {
    const next = [...messages];
    next[awaiting] = { ...next[awaiting], ...patch, puffing: false };
    return next;
  }
  return [
    ...messages,
    {
      id: `assistant-${messageId || Date.now()}`,
      role: "assistant",
      timestamp: patch.timestamp || new Date().toISOString(),
      historyMessageId: messageId,
      ...patch,
      puffing: false,
    },
  ];
}

/** An assistant text answer produced in the UI (e.g. the embedded chat's setup guidance), never sent. */
function localTextPayload(message: string): ChatOrchestrationWebSocketPayload {
  return { correlationId: "", type: "text", message, timestamp: new Date().toISOString() };
}

function jobForHistoryMessage(
  jobs: ChatTurnJobView[],
  message: ChatHistoryMessage,
  awaitingCount: number
): ChatTurnJobView | undefined {
  if (message.messageId) {
    const found = jobs.find((job) => job.messageId === message.messageId);
    if (found) return found;
  }
  if (awaitingCount === 1 && jobs.length === 1) return jobs[0];
  return undefined;
}

function formatChatHistoryDate(iso?: string): string {
  if (!iso || String(iso).trim() === "") return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function resolveAssistantResponseTypeLabel(m: ChatMessage, t: TFunction): string {
  const raw = m.historyResponseType || m.payload?.type;
  const s = raw != null && String(raw).trim() !== "" ? String(raw).trim().toLowerCase() : "text";
  return t(`chat.responseType.${s}`, { defaultValue: s });
}

/** First meaningful user ask in the thread (skip clarification Q/A blobs). */
function findOriginalUserRequest(messages: ChatMessage[]): string | undefined {
  for (const m of messages) {
    if (m.role !== "user") continue;
    const content = (m.content || "").trim();
    if (!content) continue;
    const originalPrefix = content.match(/^(?:Orijinal istek|Original request)\s*:\s*(.+)$/im);
    if (originalPrefix?.[1]?.trim()) {
      return originalPrefix[1].trim();
    }
    const lines = content.split(/\n/).map((l) => l.trim()).filter(Boolean);
    const clarificationLike = lines.filter((l) => /\?:/.test(l) || /:\s*.+/.test(l));
    const looksLikeClarification =
      clarificationLike.length >= 1 &&
      clarificationLike.length >= Math.ceil(lines.length * 0.6) &&
      /hangi|which|index|tarih|date|range|alan|field|mblfn/i.test(content);
    if (looksLikeClarification) continue;
    return content;
  }
  return undefined;
}

function autoSelectionToMessageScope(selection: ChatAutoSelection): ChatMessageScope {
  const firstOpenApi = Array.isArray(selection.openApiCatalogs)
    ? selection.openApiCatalogs.find((s) => s?.catalogId)
    : undefined;
  const indexNameById = new Map<string, string>();
  for (const d of selection.indexDetails || []) {
    if (d?.id && d?.name) indexNameById.set(d.id, d.name);
  }
  for (const c of selection.candidates || []) {
    if (c?.kind === "index" && c.id && c.name) indexNameById.set(c.id, c.name);
  }
  return {
    // Always set family keys so winner prune can clear unused OpenAPI/index/adaptor chips.
    indices: Array.isArray(selection.indices) ? selection.indices.filter(Boolean) : [],
    openApi: firstOpenApi
      ? {
          catalogId: firstOpenApi.catalogId,
          catalogName: firstOpenApi.name || firstOpenApi.catalogId || "OpenAPI",
          tag: firstOpenApi.tag || undefined,
        }
      : null,
    adaptors: Array.isArray(selection.adaptors)
      ? selection.adaptors
          .filter((a): a is NonNullable<typeof a> & { adaptorId: string } => Boolean(a?.adaptorId))
          .map((a) => ({
            id: a.adaptorId,
            name: a.name || a.adaptorId,
            type: a.type || undefined,
          }))
      : [],
    indexLabels: Object.fromEntries(indexNameById.entries()),
  };
}

function preferenceFromOutputIntent(
  intent: ChatAutoSelection["outputIntent"] | undefined
): ChatOutputPreference | null {
  if (!intent?.outputType) return null;
  const type = String(intent.outputType).trim().toLowerCase();
  if (!type || type === "anomaly" || type === "forecast") return null;
  if (!["text", "html", "table", "chart", "json", "widget"].includes(type)) return null;
  const pref: ChatOutputPreference = { outputType: type as ChatOutputType };
  if (intent.chartType && (type === "chart" || type === "widget")) {
    pref.chartType = intent.chartType;
  }
  if (type === "widget") {
    pref.widgetFormat = intent.chartType ? "echarts" : "table";
  }
  return pref;
}

/** Composer pickers follow the winning auto-selection (unused families are cleared). */
function pickersFromAutoSelection(selection: ChatAutoSelection): {
  indices: string[];
  openApi: OpenApiSelection | null;
  adaptors: ChatAdaptorSelection[];
} {
  const firstOpenApi = Array.isArray(selection.openApiCatalogs)
    ? selection.openApiCatalogs.find((s) => s?.catalogId)
    : undefined;
  return {
    indices: Array.isArray(selection.indices) ? selection.indices.filter(Boolean) : [],
    openApi: firstOpenApi?.catalogId
      ? {
          catalogId: firstOpenApi.catalogId,
          catalogName: firstOpenApi.name || firstOpenApi.catalogId,
          tag: firstOpenApi.tag || undefined,
        }
      : null,
    adaptors: Array.isArray(selection.adaptors)
      ? selection.adaptors
          .map((a) =>
            a?.adaptorId
              ? {
                  id: a.adaptorId,
                  name: a.name || a.adaptorId,
                  type: a.type || "",
                }
              : null
          )
          .filter((a): a is ChatAdaptorSelection => Boolean(a))
      : [],
  };
}

function openApiScopeLabel(scope: NonNullable<ChatMessageScope["openApi"]>): string {
  const name = scope.catalogName || scope.catalogId || "OpenAPI";
  return scope.tag ? `${name} / ${scope.tag}` : name;
}

function resourceAutoKey(kind: "index" | "adaptor" | "openapi", id: string): string {
  return `${kind}:${id}`;
}

function autoKeysFromPickers(pickers: {
  indices: string[];
  openApi: OpenApiSelection | null;
  adaptors: ChatAdaptorSelection[];
}): Set<string> {
  const keys = new Set<string>();
  for (const idx of pickers.indices) {
    if (idx) keys.add(resourceAutoKey("index", idx));
  }
  for (const a of pickers.adaptors) {
    if (a?.id) keys.add(resourceAutoKey("adaptor", a.id));
  }
  if (pickers.openApi?.catalogId) {
    keys.add(resourceAutoKey("openapi", pickers.openApi.catalogId));
  }
  return keys;
}

/** Resource present on the bubble after router merge but not in the user's committed send scope. */
function isAutoOnMessageScope(msg: ChatMessage, kind: "index" | "adaptor" | "openapi", id: string): boolean {
  const committed = msg.committedScope;
  const shown = msg.messageScope;
  if (!shown || !id) return false;
  if (kind === "index") {
    const inShown = Boolean(shown.indices?.includes(id));
    const inCommitted = Boolean(committed?.indices?.includes(id));
    return inShown && !inCommitted;
  }
  if (kind === "adaptor") {
    const inShown = Boolean(shown.adaptors?.some((a) => a.id === id));
    const inCommitted = Boolean(committed?.adaptors?.some((a) => a.id === id));
    return inShown && !inCommitted;
  }
  const shownId = shown.openApi?.catalogId;
  const committedId = committed?.openApi?.catalogId;
  return Boolean(shownId && shownId === id && committedId !== id);
}

function messageTextForSuggest(m: ChatMessage): string {
  if (m.role === "user") {
    return (m.content || "").trim();
  }
  if (m.error) {
    return m.error.trim();
  }
  const payload = m.payload;
  if (!payload) {
    return (m.content || "").trim();
  }
  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }
  if (typeof payload.error === "string" && payload.error.trim()) {
    return payload.error.trim();
  }
  const result = payload.result;
  if (typeof result === "string" && result.trim()) {
    return result.trim();
  }
  if (result && typeof result === "object" && !Array.isArray(result)) {
    const response = (result as Record<string, unknown>).response;
    if (typeof response === "string" && response.trim()) {
      return response.trim();
    }
  }
  return (m.content || "").trim();
}

function canEmailMessage(m: ChatMessage): boolean {
  if (m.role !== "assistant" || m.error) return false;
  const type = String(m.payload?.type || "").toLowerCase();
  if (type === "mutation-draft" || type === "clarification" || type === "widget-draft") return false;
  if (m.payload) {
    if (m.payload.result != null) return true;
    if (typeof m.payload.message === "string" && m.payload.message.trim()) return true;
  }
  return Boolean((m.content || "").trim());
}

function precedingUserQuestion(messages: ChatMessage[], assistantId: string): string {
  const idx = messages.findIndex((m) => m.id === assistantId);
  if (idx > 0) {
    for (let i = idx - 1; i >= 0; i -= 1) {
      if (messages[i].role === "user" && (messages[i].content || "").trim()) {
        return (messages[i].content || "").trim();
      }
    }
  }
  return findOriginalUserRequest(messages) || "";
}

/** Secondary history-rail controls behind one “more” button (keeps the header tidy). */
function ChatHistoryMoreMenu({
  historyRailExpanded,
  showHistoryFilters,
  onToggleExpand,
  onToggleFilters,
  iconSx,
  t,
}: {
  historyRailExpanded: boolean;
  showHistoryFilters: boolean;
  onToggleExpand: () => void;
  onToggleFilters: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iconSx: any;
  t: TFunction;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <AppIconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={iconSx}
        aria-label={t("navbar.more")}
      >
        <Icon fontSize="small">more_vert</Icon>
      </AppIconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onToggleExpand();
          }}
        >
          <ListItemIcon>
            {historyRailExpanded ? (
              <ChevronLeftRoundedIcon fontSize="small" />
            ) : (
              <ChevronRightRoundedIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {historyRailExpanded ? t("chat.narrowSidebar") : t("chat.expandSidebar")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onToggleFilters();
          }}
        >
          <ListItemIcon>
            <Icon fontSize="small">tune</Icon>
          </ListItemIcon>
          <ListItemText>{showHistoryFilters ? t("chat.hideFilters") : t("chat.filters")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default function ChatScreen() {
  const { t, i18n } = useTranslation();
  const host = useChatHost();
  const features = host.features;
  const { brandMark } = useClientBranding();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [formFillOpen, setFormFillOpen] = useState(false);
  const [formFillBusy, setFormFillBusy] = useState(false);
  const [formFillError, setFormFillError] = useState<string | null>(null);
  const [formFillNotice, setFormFillNotice] = useState<string | null>(null);
  const [formFillRows, setFormFillRows] = useState<FormFillRow[]>([]);
  const formFillSnapshot = useRef<FormFieldsPayload | null>(null);
  const [history, setHistory] = useState<ChatHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [showHistoryFilters, setShowHistoryFilters] = useState(false);
  const [historyTitleQuery, setHistoryTitleQuery] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [historyRailOpen, setHistoryRailOpen] = useState(false);
  const [historyRailExpanded, setHistoryRailExpanded] = useState(false);
  const [historyConversationLoading, setHistoryConversationLoading] = useState(false);
  const [input, setInput] = useState("");
  const [outputPreference, setOutputPreference] = useState<ChatOutputPreference | null>(null);
  /** True when the user picked output via slash; blocks auto-predicted overwrites. */
  const [outputPreferenceLocked, setOutputPreferenceLocked] = useState(false);
  const [outputPreferencePredicted, setOutputPreferencePredicted] = useState(false);
  const [resourceMeta, setResourceMeta] = useState<Record<string, ResourceMeta>>({});
  const [resourceCandidates, setResourceCandidates] = useState<ChatResourceCandidate[]>([]);
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashMode, setSlashMode] = useState<"commands" | "widgetFormats" | "chartTypes">("commands");
  const [slashQuery, setSlashQuery] = useState("");
  const [slashChartFor, setSlashChartFor] = useState<ChatOutputType | null>(null);
  const [slashWidgetFormat, setSlashWidgetFormat] = useState<ChatOutputPreference["widgetFormat"] | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatRuns, setChatRuns] = useState<ChatTurnJobView[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [toolPicker, setToolPicker] = useState<"index" | "adaptor" | "openapi" | null>(null);
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [mailHtml, setMailHtml] = useState(true);
  const [mailPreparing, setMailPreparing] = useState(false);
  const [mailSubmitting, setMailSubmitting] = useState(false);
  const [ragDialogOpen, setRagDialogOpen] = useState(false);
  const [ragSubmitting, setRagSubmitting] = useState(false);
  const [ragError, setRagError] = useState("");
  const [ragQuestion, setRagQuestion] = useState("");
  const [ragSummary, setRagSummary] = useState("");
  const [ragQueries, setRagQueries] = useState<ChatExecutedQuery[]>([]);
  const [ragSelected, setRagSelected] = useState<boolean[]>([]);
  const [savedExampleMessageIds, setSavedExampleMessageIds] = useState<Set<string>>(new Set());
  const [ragPromoteTarget, setRagPromoteTarget] = useState<ChatMessage | null>(null);
  const [canUseRagPromote, setCanUseRagPromote] = useState(false);
  const [fullscreenPayload, setFullscreenPayload] = useState<ChatOrchestrationWebSocketPayload | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [pageConsentOpen, setPageConsentOpen] = useState(false);
  const [pageConsentBusy, setPageConsentBusy] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const historyRailRef = useRef<HTMLDivElement | null>(null);
  const historyLoadGen = useRef(0);
  const selectedHistoryIdRef = useRef<string | null>(null);
  /** True while a history reopen is attached to an in-flight turn (not a newly sent message). */
  const resumeWatchRef = useRef(false);
  const correlationIdRef = useRef<string | undefined>(undefined);
  const firstMessageRefreshTimerRef = useRef<number | null>(null);
  const sendCooldownRef = useRef<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const outputPreferenceLockedRef = useRef(false);
  const skipPageConsentRef = useRef(false);
  const pageConsentPendingRef = useRef<{
    text: string;
    preferenceOverride?: ChatOutputPreference | null;
    options?: { preserveOutputPreference?: boolean; vpnOtpRetry?: boolean };
  } | null>(null);

  useEffect(() => {
    outputPreferenceLockedRef.current = outputPreferenceLocked;
  }, [outputPreferenceLocked]);

  useEffect(() => {
    const draft = input.trim();
    if (!draft || draft.startsWith("/") || outputPreferenceLocked || loading) {
      return;
    }
    const timer = window.setTimeout(() => {
      void predictChatOutputIntent(draft)
        .then((intent) => {
          if (outputPreferenceLockedRef.current) return;
          const confidence = intent.confidence ?? 0;
          const type = (intent.outputType || "").toLowerCase();
          // Soft text prior alone is not enough to auto-pin a chip.
          if (confidence < 0.28 || type === "text" || type === "anomaly" || type === "forecast") {
            setOutputPreference((prev) => {
              // Only clear auto-predicted chips, never a slash-locked preference.
              if (outputPreferenceLockedRef.current) return prev;
              return null;
            });
            setOutputPreferencePredicted(false);
            return;
          }
          const predicted = preferenceFromOutputIntent(intent);
          if (!predicted) return;
          setOutputPreference(predicted);
          setOutputPreferencePredicted(true);
        })
        .catch(() => {
          /* ignore draft predict errors */
        });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [input, outputPreferenceLocked, loading]);
  /** In-page “fullscreen”: fixed overlay over the viewport (not browser Fullscreen API). */
  const [isPageFullscreen, setIsPageFullscreen] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<string[]>(() => {
    const stored = readStoredIndices();
    return stored.length > 0 ? stored : host.defaultIndices ?? [];
  });
  const [selectedAdaptors, setSelectedAdaptors] = useState<ChatAdaptorSelection[]>([]);
  const [openApiSelection, setOpenApiSelection] = useState<OpenApiSelection | null>(null);
  /** Keys `index|adaptor|openapi:<id>` applied by router auto-selection (not manual picker). */
  const [autoResourceKeys, setAutoResourceKeys] = useState<Set<string>>(() => new Set());
  const [signedInUsername, setSignedInUsername] = useState("");
  const confirm = useConfirm();
  const packageLimits = usePackageLimits();

  const applyAutoSelectionPickers = useCallback((selection: ChatAutoSelection) => {
    const pickers = pickersFromAutoSelection(selection);
    setSelectedIndices(pickers.indices);
    setOpenApiSelection(pickers.openApi);
    setSelectedAdaptors(pickers.adaptors);
    setAutoResourceKeys(autoKeysFromPickers(pickers));
  }, []);

  const handleSelectedIndicesChange = useCallback((next: string[]) => {
    setSelectedIndices((prevSelected) => {
      const prevSet = new Set(prevSelected);
      setAutoResourceKeys((prev) => {
        const n = new Set(prev);
        for (const key of [...n]) {
          if (!key.startsWith("index:")) continue;
          const id = key.slice("index:".length);
          if (!next.includes(id)) {
            n.delete(key);
            continue;
          }
          // Newly added via picker/click → treat as manual selection.
          if (!prevSet.has(id)) {
            n.delete(key);
          }
        }
        return n;
      });
      return next;
    });
  }, []);

  const handleSelectedAdaptorsChange = useCallback((next: ChatAdaptorSelection[]) => {
    setSelectedAdaptors((prevSelected) => {
      const prevIds = new Set(prevSelected.map((a) => a.id));
      const nextIds = new Set(next.map((a) => a.id));
      setAutoResourceKeys((prev) => {
        const n = new Set(prev);
        for (const key of [...n]) {
          if (!key.startsWith("adaptor:")) continue;
          const id = key.slice("adaptor:".length);
          if (!nextIds.has(id) || !prevIds.has(id)) {
            n.delete(key);
          }
        }
        return n;
      });
      return next;
    });
  }, []);

  const handleOpenApiSelectionChange = useCallback((next: OpenApiSelection | null) => {
    setOpenApiSelection((prevSelected) => {
      setAutoResourceKeys((prev) => {
        const n = new Set(prev);
        for (const key of [...n]) {
          if (!key.startsWith("openapi:")) continue;
          const stillSame =
            Boolean(next?.catalogId) &&
            key === resourceAutoKey("openapi", next!.catalogId) &&
            prevSelected?.catalogId === next?.catalogId;
          if (!stillSame) {
            n.delete(key);
          }
        }
        return n;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    sessionStorage.setItem(CHAT_SELECTED_INDICES_KEY, JSON.stringify(selectedIndices));
  }, [selectedIndices]);

  useEffect(() => {
    let cancelled = false;
    authService
      .getUser()
      .then((u) => {
        if (cancelled || !u?.profile) return;
        const p = u.profile as Record<string, unknown>;
        const label =
          String(p.preferred_username || p.name || p.email || "").trim();
        setSignedInUsername(label);
      })
      .catch(() => {});
    getCurrentUserRoles()
      .then((roles) => {
        if (!cancelled) {
          setCanUseRagPromote(canAccessRoute(roles, [...EXAMPLE_SAVE_ROLES]));
        }
      })
      .catch(() => {
        if (!cancelled) setCanUseRagPromote(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const togglePageFullscreen = useCallback(() => {
    setIsPageFullscreen((v) => !v);
  }, []);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      if (firstMessageRefreshTimerRef.current != null) {
        window.clearTimeout(firstMessageRefreshTimerRef.current);
      }
    };
  }, []);

  const loadHistory = useCallback(async (options?: { silent?: boolean }) => {
    if (!features.showHistory) return;
    if (!options?.silent) setHistoryLoading(true);
    try {
      const result = await getData(
        "/chat-history/my?page=0&size=100&sort=createdDate,desc&fields=title,username,messages.status",
        { skipGlobalLoading: true }
      );
      const content = Array.isArray(result?.content) ? result.content : [];
      setHistory(content);
    } catch (e) {
      console.error("Failed to load chat history", e);
      if (!options?.silent) setHistory([]);
    } finally {
      if (!options?.silent) setHistoryLoading(false);
    }
  }, [features.showHistory]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    selectedHistoryIdRef.current = selectedHistoryId;
  }, [selectedHistoryId]);

  useEffect(() => {
    if (!historyRailOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (historyRailRef.current?.contains(target)) return;
      const element = target instanceof Element ? target : target.parentElement;
      if (element?.closest(".MuiModal-root, .MuiPopover-root, .MuiPopper-root")) return;
      setHistoryRailOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [historyRailOpen]);

  useEffect(() => {
    if (!historyRailOpen || !features.showHistory) return undefined;
    const id = window.setInterval(() => {
      void loadHistory({ silent: true });
    }, 12000);
    return () => window.clearInterval(id);
  }, [historyRailOpen, features.showHistory, loadHistory]);

  const restoreChatRuns = useCallback(async (correlationId?: string): Promise<ChatTurnJobView[]> => {
    if (!correlationId) {
      setChatRuns([]);
      return [];
    }
    try {
      const open = await chatTurnJobService.listOpen(correlationId);
      const jobs = Array.isArray(open) ? open.filter((job) => !isTerminalChatTurnStatus(job.status)) : [];
      setChatRuns(jobs);
      return jobs;
    } catch {
      setChatRuns([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!isPageFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsPageFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isPageFullscreen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const toPayload = useCallback((historyItem: ChatHistoryItem, m: ChatHistoryMessage): ChatOrchestrationWebSocketPayload => {
    const payload = normalizeChatHistoryMessage(m, {
      username: historyItem.username || historyItem.title,
      correlationId: historyItem.correlationId,
      createdDate: historyItem.createdDate,
    });
    if (m.status === "ERROR") {
      const withError = {
        ...payload,
        type: payload.type || "error",
        error: payload.error || "ORCHESTRATION_ERROR",
        message: payload.message || m.errorMessage || undefined,
      };
      return withError;
    }
    return payload;
  }, [t]);

  const handleWidgetDraftPersisted = useCallback(
    (
      chatRowId: string,
      next: { widgetDraft: unknown; savedWidgetId?: string; message?: string }
    ) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== chatRowId || !m.payload) return m;
          const prevResult =
            m.payload.result && typeof m.payload.result === "object" && !Array.isArray(m.payload.result)
              ? { ...(m.payload.result as Record<string, unknown>) }
              : {};
          if (next.savedWidgetId) {
            prevResult.savedWidgetId = next.savedWidgetId;
          }
          return {
            ...m,
            payload: {
              ...m.payload,
              widgetDraft: next.widgetDraft,
              message: next.message ?? m.payload.message,
              result: Object.keys(prevResult).length > 0 ? prevResult : m.payload.result,
            },
          };
        })
      );
    },
    []
  );

  const normalizeChatHistoryRead = (raw: unknown): Partial<ChatHistoryItem> => {
    if (!raw || typeof raw !== "object") return {};
    const o = raw as Record<string, unknown>;
    if (o.data && typeof o.data === "object") return o.data as unknown as Partial<ChatHistoryItem>;
    return o as unknown as Partial<ChatHistoryItem>;
  };

  const mapHistoryToMessages = useCallback(
    (merged: ChatHistoryItem, openJobs: ChatTurnJobView[]): ChatMessage[] => {
      const awaitingCount = (merged.messages || []).filter(isAwaitingHistoryMessage).length;
      const mapped: ChatMessage[] = [];
      (merged.messages || []).forEach((m, idx) => {
        const ts = m.createdAt || merged.createdDate || new Date().toISOString();
        if (m.userMessage) {
          const parsed = stripChatMessagePrefixes(m.userMessage);
          mapped.push({
            id: `h-user-${merged.id}-${idx}`,
            role: "user",
            content: parsed.displayText || m.userMessage,
            messageScope: hasChatMessageScope(parsed.scope) ? parsed.scope : undefined,
            timestamp: ts,
            displayUsername: merged.username || merged.title,
          });
        }
        const assistantId = m.messageId ? `h-assistant-${m.messageId}` : `h-assistant-${merged.id}-${idx}`;
        if (isAwaitingHistoryMessage(m)) {
          const job = jobForHistoryMessage(openJobs, m, awaitingCount);
          mapped.push({
            id: assistantId,
            role: "assistant",
            timestamp: ts,
            historyStatus: "PENDING",
            historyCreatedAt: m.createdAt,
            historyMessageId: m.messageId,
            progressStep: job?.step,
            progressTool: job?.tool,
            progressDetail: job?.timeline?.at(-1)?.detail,
            progressTimeline: job?.timeline,
          });
          return;
        }
        const assistantPayload = toPayload(merged, m);
        const historyError =
          m.status === "ERROR" || isOrchestrationErrorPayload(assistantPayload)
            ? resolveChatClientErrorMessage(assistantPayload, t)
            : undefined;
        mapped.push({
          id: assistantId,
          role: "assistant",
          payload: historyError ? undefined : assistantPayload,
          error: historyError,
          timestamp: ts,
          historyStatus: m.status,
          historyCreatedAt: m.createdAt,
          historyResponseType: historyError ? "error" : m.responseType,
          historyMessageId: m.messageId,
        });
      });
      return mapped;
    },
    [toPayload, t]
  );

  const absorbFinishedTurn = useCallback(
    async (historyId: string) => {
      if (selectedHistoryIdRef.current !== historyId) return;
      try {
        const raw = await getData(`/chat-history/read/${encodeURIComponent(historyId)}`, {
          skipGlobalLoading: true,
        });
        if (selectedHistoryIdRef.current !== historyId) return;
        const detail = normalizeChatHistoryRead(raw);
        const merged: ChatHistoryItem = {
          id: historyId,
          ...detail,
          messages: Array.isArray(detail.messages) ? detail.messages : [],
        };
        const jobs = await restoreChatRuns(merged.correlationId);
        if (selectedHistoryIdRef.current !== historyId) return;
        setMessages(mapHistoryToMessages(merged, jobs));
        void loadHistory({ silent: true });
      } catch (e) {
        console.error("Failed to refresh running chat", e);
      }
    },
    [mapHistoryToMessages, restoreChatRuns, loadHistory]
  );

  const selectHistory = useCallback(
    async (item: ChatHistoryItem) => {
      const gen = ++historyLoadGen.current;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      selectedHistoryIdRef.current = item.id;
      setSelectedHistoryId(item.id);
      setHistoryConversationLoading(true);
      try {
        const raw = await getData(`/chat-history/read/${encodeURIComponent(item.id)}`, {
          skipGlobalLoading: true,
        });
        if (gen !== historyLoadGen.current) return;
        const detail = normalizeChatHistoryRead(raw);
        const merged: ChatHistoryItem = {
          ...item,
          ...detail,
          id: item.id,
          messages: Array.isArray(detail.messages)
            ? detail.messages
            : Array.isArray(item.messages)
              ? item.messages
              : [],
        };
        correlationIdRef.current = merged.correlationId;
        const openJobs = await restoreChatRuns(merged.correlationId);
        if (gen !== historyLoadGen.current) return;

        setMessages(mapHistoryToMessages(merged, openJobs));
        setSuggestions([]);
        if (merged.correlationId) {
          chatQueryExampleService
            .savedMessageIds(merged.correlationId)
            .then((ids) => {
              if (gen !== historyLoadGen.current) return;
              setSavedExampleMessageIds(new Set(ids));
            })
            .catch(() => {});
        }

        const shouldWatch =
          Boolean(merged.correlationId) &&
          ((merged.messages || []).some(isAwaitingHistoryMessage) || openJobs.length > 0);
        resumeWatchRef.current = shouldWatch;
        if (shouldWatch && merged.correlationId) {
          const unsub = await subscribeToChatResponse(
            merged.correlationId,
            (payload) => {
              if (gen !== historyLoadGen.current) return;
              const interimType = String(payload.type || "").toLowerCase();
              if (interimType === "progress") {
                const resultRec =
                  payload.result && typeof payload.result === "object"
                    ? (payload.result as Record<string, unknown>)
                    : undefined;
                const frame = readProgressFrame(
                  resultRec,
                  typeof payload.message === "string" ? payload.message : undefined
                );
                setMessages((prev) =>
                  applyProgressToMessages(prev, {
                    ...frame,
                    messageId: payload.messageId,
                  })
                );
                return;
              }
              if (interimType === "warehouse-job" || interimType === "auto-selection") return;
              if (interimType === "chat-run") {
                const job = parseChatTurnJob(payload.result);
                if (!job) return;
                setChatRuns((prev) => {
                  const idx = prev.findIndex((run) => run.turnId === job.turnId);
                  if (isTerminalChatTurnStatus(job.status)) {
                    return prev.filter((run) => run.turnId !== job.turnId);
                  }
                  if (idx < 0) return [...prev, job];
                  const next = [...prev];
                  next[idx] = job;
                  return next;
                });
                if (!isTerminalChatTurnStatus(job.status)) {
                  setMessages((prev) =>
                    applyProgressToMessages(prev, {
                      step: job.step,
                      tool: job.tool,
                      timeline: job.timeline,
                      messageId: job.messageId,
                    })
                  );
                }
                return;
              }
              if (isOrchestrationErrorPayload(payload)) {
                const displayError = resolveChatClientErrorMessage(payload, t);
                setChatRuns((prev) => prev.filter((job) => job.messageId !== payload.messageId));
                setMessages((prev) =>
                  upsertAssistantTurn(prev, payload.messageId, {
                    error: displayError,
                    payload: undefined,
                    historyStatus: "ERROR",
                    historyResponseType: "error",
                    historyCreatedAt: payload.timestamp || new Date().toISOString(),
                  })
                );
                resumeWatchRef.current = false;
                void loadHistory({ silent: true });
                scrollToBottom();
                return;
              }
              setChatRuns((prev) => prev.filter((job) => job.messageId !== payload.messageId));
              setMessages((prev) =>
                upsertAssistantTurn(prev, payload.messageId, {
                  payload,
                  error: undefined,
                  historyStatus: "COMPLETED",
                  historyCreatedAt: payload.timestamp || new Date().toISOString(),
                  historyResponseType: payload.type,
                })
              );
              resumeWatchRef.current = false;
              void loadHistory({ silent: true });
              scrollToBottom();
            },
            (err) => {
              if (gen !== historyLoadGen.current) return;
              const errorText =
                err === CHAT_RESPONSE_TIMEOUT_ERROR ? t("chat.responseTimeout") : t("chat.genericError");
              setMessages((prev) => {
                const idx = findAwaitingIndex(prev);
                if (idx < 0) return prev;
                const current = prev[idx];
                if (current.payload && !current.error && current.historyStatus === "COMPLETED") return prev;
                const next = [...prev];
                next[idx] = {
                  ...current,
                  error: errorText,
                  historyStatus: "ERROR",
                  historyResponseType: "error",
                  historyCreatedAt: new Date().toISOString(),
                };
                return next;
              });
            }
          );
          if (gen !== historyLoadGen.current) {
            unsub();
            return;
          }
          unsubscribeRef.current = unsub;
        }
        setTimeout(() => scrollToBottom(), 50);
      } catch (e) {
        if (gen !== historyLoadGen.current) return;
        console.error("Failed to load chat history detail", e);
        setMessages([]);
        window.dispatchEvent(
          new CustomEvent("showGlobalNotification", {
            detail: {
              color: "error",
              icon: "error",
              title: t("chat.historyTitle"),
              reason: t("chat.historyLoadReason"),
              content: t("chat.historyLoadFailed"),
              info: "CHAT_HISTORY_READ_ERROR",
            },
          })
        );
      } finally {
        if (gen === historyLoadGen.current) setHistoryConversationLoading(false);
      }
    },
    [scrollToBottom, mapHistoryToMessages, restoreChatRuns, loadHistory, t]
  );

  const submitMessage = useCallback(
    async (
      text: string,
      preferenceOverride?: ChatOutputPreference | null,
      options?: { preserveOutputPreference?: boolean; vpnOtpRetry?: boolean }
    ) => {
      const trimmed = text.trim();
      if (trimmed.startsWith("/") || (!trimmed && !preferenceOverride && !outputPreference) || loading) {
        return;
      }

      // Embedded chat with incomplete workspace setup: answer locally, nothing is sent.
      const setupReply = trimmed ? host.setupGate?.replyIfBlocked() ?? null : null;
      if (setupReply) {
        const now = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: "user",
            content: trimmed,
            timestamp: now,
            displayUsername: signedInUsername || undefined,
          },
          {
            id: `assistant-setup-${Date.now()}`,
            role: "assistant",
            timestamp: now,
            payload: localTextPayload(setupReply),
          },
        ]);
        setInput("");
        scrollToBottom();
        return;
      }

      if (isEmbedPageContentEnabled() && looksLikePageIntent(trimmed) && !skipPageConsentRef.current) {
        const embedKey = embedRuntimeKey();
        const nextCorrelationId = ensureCorrelationId(correlationIdRef.current);
        correlationIdRef.current = nextCorrelationId;
        const pageUrl = embedPageUrl();
        const consented =
          !!embedKey && !!pageUrl && hasPageContentConsent(embedKey, pageUrl, nextCorrelationId);
        if (embedKey && !consented) {
          pageConsentPendingRef.current = { text, preferenceOverride, options };
          setPageConsentOpen(true);
          return;
        }
        if (embedKey && consented && !embedPageSummaryForSend(embedKey, nextCorrelationId)) {
          try {
            const payload = await requestHostPageText();
            setEmbedPageUrl(payload.pageUrl);
            await summarizeHostPage(payload, i18n.language);
          } catch (e) {
            console.warn("[embed page content] summarize failed", e);
          }
        }
      }
      skipPageConsentRef.current = false;

      const activePreference = preferenceOverride !== undefined ? preferenceOverride : outputPreference;
      const isFirstMessageInEmptyChat = messages.length === 0;
      const openApiPrefix = formatOpenApiMessagePrefix(openApiSelection);
      const adaptorPrefix = selectedAdaptors
        .map((a) => `Adaptor: ${a.id}`)
        .join(" ");
      let outbound =
        openApiPrefix && !trimmed.toLowerCase().includes("openapi katalog:")
          ? `${openApiPrefix} ${trimmed}`.trim()
          : trimmed;
      if (adaptorPrefix && !/adaptor\s*:/i.test(outbound)) {
        outbound = `${adaptorPrefix} ${outbound}`.trim();
      }

      const parsedOutbound = stripChatMessagePrefixes(outbound);
      const messageScope = mergeChatMessageScope(parsedOutbound.scope, {
        indices: selectedIndices.length > 0 ? [...selectedIndices] : undefined,
        openApi: openApiSelection
          ? {
              catalogId: openApiSelection.catalogId,
              catalogName: openApiSelection.catalogName,
              tag: openApiSelection.tag,
            }
          : null,
        adaptors: selectedAdaptors.length > 0 ? selectedAdaptors.map((a) => ({ ...a })) : undefined,
      });

      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: parsedOutbound.displayText || trimmed,
        committedScope: messageScope,
        messageScope,
        timestamp: new Date().toISOString(),
        displayUsername: signedInUsername || undefined,
      };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setSuggestions([]);
      if (!options?.preserveOutputPreference) {
        setOutputPreference(null);
        setOutputPreferenceLocked(false);
        setOutputPreferencePredicted(false);
      }
      setSlashOpen(false);
      setSlashMode("commands");
      setSlashChartFor(null);
      setSlashWidgetFormat(null);
      setLoading(true);

      const placeholderId = `assistant-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: placeholderId,
          role: "assistant",
          timestamp: new Date().toISOString(),
        },
      ]);
      scrollToBottom();

      try {
        const nextCorrelationId = ensureCorrelationId(correlationIdRef.current);
        const sendResult = await sendChatMessage(
          outbound,
          nextCorrelationId,
          selectedIndices.length > 0 ? selectedIndices : undefined,
          preferenceToApiBody(activePreference)
        );
        const correlationId = sendResult.correlationId;
        const pendingMessageId = sendResult.messageId;
        correlationIdRef.current = correlationId;
        void loadHistory({ silent: true });
        setLoading(false);

        unsubscribeRef.current?.();
        resumeWatchRef.current = false;
        unsubscribeRef.current = await subscribeToChatResponse(
          correlationId,
          (payload) => {
            const enrichedPayload: ChatOrchestrationWebSocketPayload = {
              ...payload,
              messageId: payload.messageId || pendingMessageId,
            };
            const interimType = String(enrichedPayload.type || "").toLowerCase();
            if (interimType === "progress") {
              const resultRec =
                enrichedPayload.result && typeof enrichedPayload.result === "object"
                  ? (enrichedPayload.result as Record<string, unknown>)
                  : undefined;
              const frame = readProgressFrame(
                resultRec,
                typeof enrichedPayload.message === "string" ? enrichedPayload.message : undefined
              );
              if (frame.step) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === placeholderId
                      ? {
                          ...m,
                          progressStep: frame.step,
                          progressTool: frame.tool,
                          progressDetail: frame.detail,
                          progressTimeline: frame.timeline || [
                            ...(m.progressTimeline || []),
                            { step: frame.step, tool: frame.tool, detail: frame.detail },
                          ],
                        }
                      : m
                  )
                );
                scrollToBottom();
              }
              return;
            }
            if (interimType === "warehouse-job") {
              // Background warehouse job frames are consumed by WarehouseJobCard's own subscription.
              return;
            }
            if (interimType === "chat-run") {
              const job = parseChatTurnJob(enrichedPayload.result);
              if (!job) return;
              setChatRuns((prev) => {
                const idx = prev.findIndex((item) => item.turnId === job.turnId);
                if (isTerminalChatTurnStatus(job.status)) {
                  return prev.filter((item) => item.turnId !== job.turnId);
                }
                if (idx < 0) return [...prev, job];
                const next = [...prev];
                next[idx] = job;
                return next;
              });
              if (job.background || job.status === "BACKGROUND") {
                setMessages((prev) =>
                  prev.map((m) => (m.id === placeholderId && !m.payload && !m.error ? { ...m, puffing: true } : m))
                );
                window.setTimeout(() => {
                  setMessages((prev) =>
                    prev.filter((m) => m.id !== placeholderId || Boolean(m.payload || m.error))
                  );
                }, 450);
              }
              return;
            }
            if (interimType === "auto-selection") {
              const selection = enrichedPayload.autoSelection || undefined;
              setMessages((prev) => {
                let priorUserIdx = -1;
                for (let i = prev.length - 1; i >= 0; i -= 1) {
                  if (prev[i].id === placeholderId) continue;
                  if (prev[i].role === "user") {
                    priorUserIdx = i;
                    break;
                  }
                }
                const scopePatch = selection ? autoSelectionToMessageScope(selection) : undefined;
                const hasResources =
                  Boolean(selection?.indices?.length) ||
                  Boolean(selection?.openApiCatalogs?.length) ||
                  Boolean(selection?.adaptors?.length);
                return prev.map((m, i) => {
                  if (m.id === placeholderId) {
                    return {
                      ...m,
                      autoSelection: selection
                        ? { ...m.autoSelection, ...selection }
                        : m.autoSelection,
                    };
                  }
                  if (scopePatch && hasResources && i === priorUserIdx) {
                    // Auto-selection is authoritative for its families (winner replaces peers).
                    return {
                      ...m,
                      messageScope: mergeChatMessageScope(m.committedScope, scopePatch),
                    };
                  }
                  return m;
                });
              });
              if (selection) {
                setResourceMeta((prev) => mergeResourceMetaFromSelection(selection, prev));
                const ranked = rankedCandidatesForDisplay(selection);
                if (ranked.length) setResourceCandidates(ranked);
                const predicted = preferenceFromOutputIntent(selection.outputIntent);
                if (predicted && !outputPreferenceLockedRef.current) {
                  setOutputPreference(predicted);
                  setOutputPreferencePredicted(true);
                }
                const hasResources =
                  Boolean(selection.indices?.length) ||
                  Boolean(selection.openApiCatalogs?.length) ||
                  Boolean(selection.adaptors?.length);
                if (hasResources) {
                  applyAutoSelectionPickers(selection);
                }
              }
              scrollToBottom();
              return;
            }
            if (isOrchestrationErrorPayload(enrichedPayload)) {
              const displayError = resolveChatClientErrorMessage(enrichedPayload, t);
              setChatRuns((prev) => prev.filter((job) => job.messageId !== pendingMessageId));
              setMessages((prev) => {
                const patch = {
                  error: displayError,
                  historyStatus: "ERROR" as const,
                  historyResponseType: "error",
                  historyCreatedAt: enrichedPayload.timestamp || new Date().toISOString(),
                };
                if (prev.some((m) => m.id === placeholderId)) {
                  return prev.map((m) => (m.id === placeholderId ? { ...m, puffing: false, ...patch } : m));
                }
                return [
                  ...prev,
                  {
                    id: `assistant-${pendingMessageId}`,
                    role: "assistant" as const,
                    timestamp: patch.historyCreatedAt,
                    ...patch,
                  },
                ];
              });
              setLoading(false);
              scrollToBottom();
              return;
            }
            const vpnOtp = enrichedPayload.vpnOtpRequired;
            if (vpnOtp?.vpnProfileId && !options?.vpnOtpRetry) {
              // A tool needed a VPN one-time password: ask for it, then send the same question again once.
              void requestVpnOtp(vpnOtp).then((connected) => {
                if (connected) {
                  void submitMessageRef.current?.(trimmed, preferenceOverride, {
                    preserveOutputPreference: true,
                    vpnOtpRetry: true,
                  });
                }
              });
            }
            const selectionFromPayload = enrichedPayload.autoSelection;
            setChatRuns((prev) => prev.filter((job) => job.messageId !== pendingMessageId));
            setMessages((prev) => {
              const selection =
                selectionFromPayload ||
                prev.find((m) => m.id === placeholderId)?.autoSelection;
              let priorUserIdx = -1;
              if (selection) {
                for (let i = prev.length - 1; i >= 0; i -= 1) {
                  if (prev[i].id === placeholderId) continue;
                  if (prev[i].role === "user") {
                    priorUserIdx = i;
                    break;
                  }
                }
              }
              const scopePatch = selection ? autoSelectionToMessageScope(selection) : undefined;
              const completed = {
                payload: {
                  ...enrichedPayload,
                  autoSelection: selection || enrichedPayload.autoSelection,
                },
                autoSelection: selection,
                historyStatus: "COMPLETED" as const,
                historyCreatedAt: enrichedPayload.timestamp || new Date().toISOString(),
                historyResponseType: enrichedPayload.type,
                puffing: false,
              };
              if (!prev.some((m) => m.id === placeholderId)) {
                return [
                  ...prev,
                  {
                    id: `assistant-${pendingMessageId}`,
                    role: "assistant" as const,
                    timestamp: completed.historyCreatedAt,
                    ...completed,
                  },
                ];
              }
              return prev.map((m, i) => {
                if (m.id === placeholderId) {
                  return {
                    ...m,
                    ...completed,
                    autoSelection: selection || m.autoSelection,
                  };
                }
                if (scopePatch && i === priorUserIdx) {
                  return {
                    ...m,
                    messageScope: mergeChatMessageScope(m.committedScope, scopePatch),
                  };
                }
                return m;
              });
            });
            if (selectionFromPayload) {
              setResourceMeta((prev) => mergeResourceMetaFromSelection(selectionFromPayload, prev));
              const ranked = rankedCandidatesForDisplay(selectionFromPayload);
              if (ranked.length) setResourceCandidates(ranked);
              const predicted = preferenceFromOutputIntent(selectionFromPayload.outputIntent);
              if (predicted && !outputPreferenceLockedRef.current) {
                setOutputPreference(predicted);
                setOutputPreferencePredicted(true);
              }
              const hasResources =
                Boolean(selectionFromPayload.indices?.length) ||
                Boolean(selectionFromPayload.openApiCatalogs?.length) ||
                Boolean(selectionFromPayload.adaptors?.length);
              if (hasResources) {
                applyAutoSelectionPickers(selectionFromPayload);
              }
            }
            setLoading(false);
            scrollToBottom();
          },
          (err) => {
            const errorText =
              err === CHAT_RESPONSE_TIMEOUT_ERROR
                ? t("chat.responseTimeout")
                : t("chat.genericError");
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== placeholderId) return m;
                // Do not clobber a successful table/chart payload with a late WS parse noise.
                if (m.payload && !m.error && m.historyStatus === "COMPLETED") {
                  return m;
                }
                return {
                  ...m,
                  error: errorText,
                  historyStatus: "ERROR",
                  historyResponseType: "error",
                  historyCreatedAt: new Date().toISOString(),
                };
              })
            );
            setLoading(false);
            scrollToBottom();
          }
        );

        if (isFirstMessageInEmptyChat) {
          if (firstMessageRefreshTimerRef.current != null) {
            window.clearTimeout(firstMessageRefreshTimerRef.current);
          }
          firstMessageRefreshTimerRef.current = window.setTimeout(() => {
            void loadHistory({ silent: true });
            firstMessageRefreshTimerRef.current = null;
          }, 3000);
        }
      } catch (e: any) {
        const setupReply =
          e?.response?.status === 428 && host.setupGate ? host.setupGate.onSetupRequired() : null;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholderId
              ? setupReply
                ? { ...m, payload: localTextPayload(setupReply) }
                : {
                    ...m,
                    error: t("chat.sendFailed"),
                    historyStatus: "ERROR",
                    historyResponseType: "error",
                  }
              : m
          )
        );
        setLoading(false);
        scrollToBottom();
      }
    },
    [
      loading,
      messages.length,
      scrollToBottom,
      loadHistory,
      signedInUsername,
      selectedIndices,
      selectedAdaptors,
      openApiSelection,
      outputPreference,
      applyAutoSelectionPickers,
      t,
      i18n.language,
      host.setupGate,
    ]
  );
  const submitMessageRef = useRef(submitMessage);
  submitMessageRef.current = submitMessage;

  const handlePageContentConsentApprove = useCallback(async () => {
    const pending = pageConsentPendingRef.current;
    if (!pending) {
      setPageConsentOpen(false);
      return;
    }
    setPageConsentBusy(true);
    try {
      const embedKey = embedRuntimeKey();
      const correlationId = ensureCorrelationId(correlationIdRef.current);
      correlationIdRef.current = correlationId;
      const payload = await requestHostPageText();
      setEmbedPageUrl(payload.pageUrl);
      if (embedKey) {
        grantPageContentConsent(embedKey, payload.pageUrl, correlationId);
      }
      await summarizeHostPage(payload, i18n.language);
      setPageConsentOpen(false);
      pageConsentPendingRef.current = null;
      await submitMessageRef.current(pending.text, pending.preferenceOverride, pending.options);
    } catch (e) {
      console.warn("[embed page content] consent approve failed", e);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "warning",
            icon: "warning",
            title: t("chat.pageContent.consentTitle"),
            content: t("chat.pageContent.failed"),
          },
        })
      );
      skipPageConsentRef.current = true;
      setPageConsentOpen(false);
      pageConsentPendingRef.current = null;
      await submitMessageRef.current(pending.text, pending.preferenceOverride, pending.options);
    } finally {
      setPageConsentBusy(false);
    }
  }, [i18n.language, t]);

  const handlePageContentConsentReject = useCallback(() => {
    const pending = pageConsentPendingRef.current;
    skipPageConsentRef.current = true;
    setPageConsentOpen(false);
    pageConsentPendingRef.current = null;
    if (pending) {
      void submitMessageRef.current(pending.text, pending.preferenceOverride, pending.options);
    }
  }, []);

  const handleComposerInputChange = useCallback((value: string) => {
    setInput(value);
    if (value.startsWith("/")) {
      setSlashOpen(true);
      setSlashMode("commands");
      setSlashChartFor(null);
      setSlashWidgetFormat(null);
      const token = value.slice(1).split(/\s/)[0] ?? "";
      setSlashQuery(token);
      return;
    }
    setSlashOpen(false);
    setSlashMode("commands");
    setSlashQuery("");
  }, []);

  const handleSelectSlashCommand = useCallback((cmd: ChatSlashCommand) => {
    if (cmd.pickWidgetFormat) {
      setSlashMode("widgetFormats");
      setSlashChartFor("widget");
      setSlashWidgetFormat(null);
      setSlashQuery("");
      setInput((prev) => (prev.startsWith("/") ? "" : prev));
      setSlashOpen(true);
      return;
    }
    if (cmd.pickChartType) {
      setSlashMode("chartTypes");
      setSlashChartFor(cmd.id);
      setSlashQuery("");
      setInput((prev) => (prev.startsWith("/") ? "" : prev));
      setSlashOpen(true);
      return;
    }
    setOutputPreference({ outputType: cmd.id });
    setOutputPreferenceLocked(true);
    setOutputPreferencePredicted(false);
    setInput((prev) => (prev.startsWith("/") ? "" : prev));
    setSlashOpen(false);
    setSlashMode("commands");
    setSlashChartFor(null);
    setSlashWidgetFormat(null);
  }, []);

  const handleSelectWidgetFormat = useCallback((format: { id: ChatOutputPreference["widgetFormat"]; pickChartType?: boolean }) => {
    if (!format.id) return;
    if (format.pickChartType) {
      setSlashMode("chartTypes");
      setSlashChartFor("widget");
      setSlashWidgetFormat(format.id);
      setSlashQuery("");
      setInput((prev) => (prev.startsWith("/") ? "" : prev));
      setSlashOpen(true);
      return;
    }
    setOutputPreference({ outputType: "widget", widgetFormat: format.id });
    setOutputPreferenceLocked(true);
    setOutputPreferencePredicted(false);
    setSlashChartFor(null);
    setSlashWidgetFormat(null);
    setSlashOpen(false);
    setSlashMode("commands");
    setInput((prev) => (prev.startsWith("/") ? "" : prev));
  }, []);

  const handleSelectChartType = useCallback(
    (chartType: string) => {
      if (slashChartFor === "widget") {
        setOutputPreference({
          outputType: "widget",
          widgetFormat: slashWidgetFormat ?? "echarts",
          chartType,
        });
      } else {
        setOutputPreference({ outputType: "chart", chartType });
      }
      setOutputPreferenceLocked(true);
      setOutputPreferencePredicted(false);
      setSlashChartFor(null);
      setSlashWidgetFormat(null);
      setSlashOpen(false);
      setSlashMode("commands");
      setInput((prev) => (prev.startsWith("/") ? "" : prev));
    },
    [slashChartFor, slashWidgetFormat]
  );

  const handleSend = useCallback(() => {
    if (loading || sendCooldownRef.current != null) return;
    submitMessage(input);
    sendCooldownRef.current = window.setTimeout(() => {
      sendCooldownRef.current = null;
    }, 400);
  }, [input, submitMessage, loading]);

  const handleSuggestPrompts = useCallback(async () => {
    if (suggestLoading || loading) return;
    setSuggestLoading(true);
    try {
      const recentMessages = messages
        .slice(-6)
        .map((m) => {
          const content = messageTextForSuggest(m);
          if (!content) return null;
          return { role: m.role, content } as const;
        })
        .filter((m): m is { role: "user" | "assistant"; content: string } => m != null);

      const list = await fetchSuggestedPrompts({
        message: input.trim() || undefined,
        correlationId: correlationIdRef.current,
        indices: selectedIndices.length > 0 ? selectedIndices : undefined,
        locale: i18n.language,
        recentMessages: recentMessages.length > 0 ? recentMessages : undefined,
        openApiCatalogId: openApiSelection?.catalogId,
        openApiTag: openApiSelection?.tag,
      });
      setSuggestions(list);
      if (list.length === 0) {
        window.dispatchEvent(
          new CustomEvent("showGlobalNotification", {
            detail: {
              color: "warning",
              icon: "warning",
              title: t("chat.suggest.title"),
              content: t("chat.suggest.empty"),
              info: "CHAT_SUGGEST_EMPTY",
            },
          })
        );
      }
    } catch (e) {
      console.error("Failed to fetch prompt suggestions", e);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "error",
            icon: "error",
            title: t("chat.suggest.title"),
            content: t("chat.suggest.failed"),
            info: "CHAT_SUGGEST_ERROR",
          },
        })
      );
    } finally {
      setSuggestLoading(false);
    }
  }, [suggestLoading, loading, messages, input, selectedIndices, openApiSelection, i18n.language, t]);

  const openMailDialogForMessage = useCallback(
    async (msg: ChatMessage) => {
      setMailTo("");
      setMailSubject(t("chat.mail.defaultSubject"));
      setMailBody("");
      setMailHtml(true);
      setMailPreparing(true);
      setMailDialogOpen(true);
      try {
        const extracted = await buildChatEmailBodyFromMessage(msg);
        setMailBody(extracted.body);
        setMailHtml(extracted.html || Boolean(extracted.body.trim()));
      } catch (e) {
        console.error("Failed to build email body from chat result", e);
        setMailDialogOpen(false);
        window.dispatchEvent(
          new CustomEvent("showGlobalNotification", {
            detail: {
              color: "error",
              icon: "error",
              title: t("chat.mail.dialogTitle"),
              content: t("chat.mail.prepareFailed"),
              info: "CHAT_MAIL_PREPARE_ERROR",
            },
          })
        );
      } finally {
        setMailPreparing(false);
      }
    },
    [t]
  );

  const openRagDialogForMessage = useCallback(
    (msg: ChatMessage) => {
      const queries = extractExampleQueries(msg);
      if (queries.length === 0) return;
      setRagPromoteTarget(msg);
      setRagQuestion(precedingUserQuestion(messages, msg.id));
      setRagSummary((msg.payload?.message || msg.content || "").trim());
      setRagQueries(queries);
      setRagSelected(queries.map(() => true));
      setRagError("");
      setRagDialogOpen(true);
    },
    [messages]
  );

  const submitRagPromote = useCallback(async () => {
    if (!ragPromoteTarget) return;
    const queries = ragQueries.filter((_, i) => ragSelected[i]);
    if (!ragQuestion.trim() || queries.length === 0) {
      setRagError(t("chat.rag.missingFields"));
      return;
    }
    setRagSubmitting(true);
    setRagError("");
    try {
      const messageId = ragPromoteTarget.payload?.messageId;
      await chatQueryExampleService.promote({
        userQuestion: ragQuestion.trim(),
        answerSummary: ragSummary.trim(),
        queries: queries.map((q) => ({ ...q, kind: q.kind.toUpperCase() as "OPENSEARCH" | "SQL" | "MONGO" })),
        tools: exampleTools(queries),
        resources: exampleResources(queries, ragPromoteTarget.payload?.autoSelection),
        correlationId: ragPromoteTarget.payload?.correlationId || correlationIdRef.current,
        messageId,
        responseType: String(ragPromoteTarget.payload?.type || ragPromoteTarget.historyResponseType || ""),
      });
      if (messageId) setSavedExampleMessageIds((prev) => new Set(prev).add(messageId));
      setRagDialogOpen(false);
      setRagPromoteTarget(null);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("chat.rag.action"),
            content: t("chat.rag.success"),
            info: "CHAT_RAG_PROMOTE_OK",
          },
        })
      );
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        const messageId = ragPromoteTarget.payload?.messageId;
        if (messageId) setSavedExampleMessageIds((prev) => new Set(prev).add(messageId));
      }
      const msg =
        status === 409
          ? t("chat.rag.duplicate")
          : e?.response?.data?.message || e?.message || t("chat.rag.failed");
      setRagError(String(msg));
    } finally {
      setRagSubmitting(false);
    }
  }, [ragPromoteTarget, ragQueries, ragSelected, ragQuestion, ragSummary, t]);

  const submitMailDraft = useCallback(async () => {
    if (!mailTo.trim()) {
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "warning",
            icon: "warning",
            title: t("chat.mail.dialogTitle"),
            content: t("chat.mail.toRequired"),
            info: "CHAT_MAIL_TO_REQUIRED",
          },
        })
      );
      return;
    }
    if (!mailBody.trim()) {
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "warning",
            icon: "warning",
            title: t("chat.mail.dialogTitle"),
            content: t("chat.mail.bodyRequired"),
            info: "CHAT_MAIL_BODY_REQUIRED",
          },
        })
      );
      return;
    }
    setMailSubmitting(true);
    try {
      const correlationId = ensureCorrelationId(correlationIdRef.current);
      correlationIdRef.current = correlationId;
      const payload = await createChatMailDraft({
        to: mailTo.trim(),
        subject: mailSubject.trim() || t("chat.mail.defaultSubject"),
        body: mailBody,
        html: mailHtml,
        summary: t("chat.mail.draftTitle"),
        correlationId,
      });
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-mail-${Date.now()}`,
          role: "assistant",
          payload,
          timestamp: new Date().toISOString(),
          historyStatus: "COMPLETED",
          historyResponseType: "mutation-draft",
        },
      ]);
      setMailDialogOpen(false);
      setTimeout(() => scrollToBottom(), 50);
    } catch (e) {
      const warning = recipientDomainNotAllowedText((e as { response?: { data?: unknown } })?.response?.data);
      if (warning) {
        return;
      }
      console.error("Failed to create mail draft", e);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "error",
            icon: "error",
            title: t("chat.mail.dialogTitle"),
            content: e instanceof Error ? e.message : t("chat.mail.failed"),
            info: "CHAT_MAIL_DRAFT_ERROR",
          },
        })
      );
    } finally {
      setMailSubmitting(false);
    }
  }, [mailTo, mailBody, mailSubject, mailHtml, t, scrollToBottom]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && slashOpen) {
      e.preventDefault();
      setSlashOpen(false);
      setSlashMode("commands");
      setSlashChartFor(null);
      setSlashWidgetFormat(null);
      if (input.startsWith("/")) setInput("");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      if (slashOpen && input.startsWith("/")) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      handleSend();
    }
  };

  const toggleSpeechToText = () => {
    const w = window as any;
    const SpeechRecognitionCtor = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "warning",
            icon: "mic_off",
            title: t("chat.speechTitle"),
            reason: t("chat.speechReason"),
            content: t("chat.speechNotSupported"),
            info: "SPEECH_NOT_SUPPORTED",
          },
        })
      );
      return;
    }

    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = navigator.language || "tr-TR";
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results || [])
        .map((result: any) => result?.[0]?.transcript || "")
        .join(" ")
        .trim();

      if (transcript) {
        setInput((prev) => {
          const base = prev.trim();
          return base ? `${base} ${transcript}` : transcript;
        });
      }
    };

    recognition.onerror = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const theme = useTheme();
  const custom = useAppCustomStyles();
  const chat = custom.components.reportChatScreen;
  const c = useMemo(() => getReportChatShell(theme), [theme]);

  const formatDateTime = (value?: string) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  const truncateTitle = (title?: string, max = 20) => {
    const label = title || t("chat.untitled");
    return label.length > max ? `${label.slice(0, max)}...` : label;
  };

  const filteredHistory = history.filter((h) => {
    const titleQuery = historyTitleQuery.trim().toLowerCase();
    const titleOk =
      !titleQuery ||
      String(h.title || "").toLowerCase().includes(titleQuery) ||
      String(h.username || "").toLowerCase().includes(titleQuery);

    const createdAt = h.createdDate ? new Date(h.createdDate).getTime() : NaN;
    const fromTs = historyDateFrom ? new Date(historyDateFrom).getTime() : NaN;
    const toTs = historyDateTo ? new Date(historyDateTo).getTime() : NaN;

    const fromOk = Number.isNaN(fromTs) || (!Number.isNaN(createdAt) && createdAt >= fromTs);
    const toOk = Number.isNaN(toTs) || (!Number.isNaN(createdAt) && createdAt <= toTs);
    return titleOk && fromOk && toOk;
  });

  const openThreadAwaiting = messages.some((m) => isAwaitingAssistant(m));
  const visibleHistory = [...filteredHistory].sort((a, b) => {
    const rank = (item: ChatHistoryItem) =>
      historyItemIsRunning(item) || (item.id === selectedHistoryId && openThreadAwaiting) ? 0 : 1;
    return rank(a) - rank(b);
  });

  const handleDeleteHistory = async (recordId: string) => {
    try {
      await confirm({
        title: t("chat.deleteHistoryTitle"),
        description: t("chat.deleteHistoryDescription"),
        confirmationText: t("common.delete"),
        cancellationText: t("common.cancel"),
      });

      await deleteData(`/chat-history/delete/${recordId}`);
      if (selectedHistoryId === recordId) {
        setSelectedHistoryId(null);
        setMessages([]);
        correlationIdRef.current = undefined;
      }
      await loadHistory();
    } catch (e) {
      // User may cancel the dialog; silently ignore cancellations.
      if ((e as any)?.name === "CancelError") return;
      console.error("Failed to delete chat history", e);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "error",
            icon: "error",
            title: t("common.delete"),
            reason: t("chat.historyTitle"),
            content: t("chat.deleteHistoryFailed"),
            info: "DELETE_ERROR",
          },
        })
      );
    }
  };

  const startNewChat = () => {
    historyLoadGen.current += 1;
    selectedHistoryIdRef.current = null;
    resumeWatchRef.current = false;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setSelectedHistoryId(null);
    setMessages([]);
    setInput("");
    setSuggestions([]);
    setLoading(false);
    correlationIdRef.current = undefined;
    setChatRuns([]);
    setSelectedIndices([]);
    setSelectedAdaptors([]);
    setOpenApiSelection(null);
    setAutoResourceKeys(new Set());
    setResourceMeta({});
    setResourceCandidates([]);
    setOutputPreference(null);
    setOutputPreferenceLocked(false);
    setOutputPreferencePredicted(false);
    setSlashOpen(false);
    setSlashMode("commands");
    setSlashQuery("");
    setSlashChartFor(null);
    setSlashWidgetFormat(null);
    try {
      sessionStorage.removeItem(CHAT_SELECTED_INDICES_KEY);
    } catch {
      /* ignore */
    }
  };

  const handlePrintPayload = (payload: ChatOrchestrationWebSocketPayload) => {
    const type = String(payload.type || "").toLowerCase();
    const message = payload.message ? `<h3>${String(payload.message)}</h3>` : "";
    let body = "";

    const resultHtmlFromResponse = (): string => {
      const r = payload.result;
      if (typeof r === "string") return r;
      if (r && typeof r === "object" && "response" in (r as Record<string, unknown>)) {
        return String((r as Record<string, unknown>).response || "");
      }
      return "";
    };

    if (type === "table") {
      const html = resultHtmlFromResponse();
      body = html || `<p>${t("chat.print.noTable")}</p>`;
    } else if (type === "html") {
      const doc = resultHtmlFromResponse();
      if (doc && /<!doctype html/i.test(doc.trim())) {
        const printWindow = window.open("", "_blank", "width=1100,height=800");
        if (!printWindow) return;
        printWindow.document.write(doc);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        return;
      }
      body = doc
        ? doc
        : `<p>${t("chat.print.noHtml")}</p>`;
    } else if (type === "json") {
      const pretty =
        payload.result == null
          ? ""
          : typeof payload.result === "string"
            ? (() => {
                try {
                  return JSON.stringify(JSON.parse(payload.result), null, 2);
                } catch {
                  return payload.result;
                }
              })()
            : JSON.stringify(payload.result, null, 2);
      body = `<pre>${String(pretty || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>`;
    } else if (type === "chart") {
      const option =
        typeof payload.result === "string"
          ? payload.result
          : JSON.stringify(payload.result, null, 2);
      body = `<p>${t("chat.print.chartData")}</p><pre>${String(option || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>`;
    } else {
      const text =
        payload.result == null
          ? ""
          : typeof payload.result === "string"
            ? payload.result
            : JSON.stringify(payload.result, null, 2);
      body = `<pre>${String(text || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>`;
    }

    const printWindow = window.open("", "_blank", "width=1100,height=800");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>${t("chat.print.title")}</title>
          <style>${chat.printDocumentCss}</style>
        </head>
        <body>
          ${message}
          ${body}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const formFillAnswer = useMemo(() => {
    if (!isFormFillEnabled(host)) return "";
    if (messages.some((m) => isAwaitingAssistant(m) || m.puffing)) return "";
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "assistant") return copyableChatText(messages[i]);
    }
    return "";
  }, [host, messages]);

  const handleFormFill = async () => {
    if (!formFillAnswer || formFillBusy) return;
    setFormFillBusy(true);
    setFormFillError(null);
    setFormFillNotice(null);
    setFormFillRows([]);
    setFormFillOpen(true);
    try {
      const snapshot = await requestHostFormFields();
      formFillSnapshot.current = snapshot;
      const visible = snapshot.forms.reduce((count, form) => count + form.fields.length, 0);
      if (visible === 0) {
        setFormFillError(t("chat.formFill.emptyFields"));
        return;
      }
      const suggestions = keepKnownSuggestions(snapshot.forms, await suggestFormFill(formFillAnswer, snapshot.forms));
      const rows = suggestions.flatMap((suggestion) => {
        const form = snapshot.forms.find((item) => item.formKey === suggestion.formKey);
        const field = form?.fields.find((item) => item.fieldKey === suggestion.fieldKey);
        if (!form || !field) return [];
        return [{
          formKey: form.formKey,
          formLabel: form.formLabel,
          fieldKey: field.fieldKey,
          meaning: field.meaning,
          value: suggestion.value,
        }];
      });
      setFormFillRows(rows);
      if (rows.length === 0) setFormFillError(t("chat.formFill.emptySuggestions"));
    } catch (error) {
      setFormFillError(
        error instanceof FormFillBridgeError && error.code === "timeout"
          ? t("chat.formFill.outdated")
          : t("chat.formFill.failed")
      );
    } finally {
      setFormFillBusy(false);
    }
  };

  const confirmFormFill = async () => {
    const snapshot = formFillSnapshot.current;
    if (!snapshot || formFillBusy) return;
    setFormFillBusy(true);
    setFormFillError(null);
    try {
      const known = new Set(
        snapshot.forms.flatMap((form) => form.fields.map((field) => `${form.formKey}\0${field.fieldKey}`))
      );
      const values = formFillRows
        .filter((row) => known.has(`${row.formKey}\0${row.fieldKey}`))
        .map((row) => ({ formKey: row.formKey, fieldKey: row.fieldKey, value: row.value }));
      const result = await applyHostFormValues(values);
      const notice = [t("chat.formFill.applied", { count: result.applied.length })];
      if (result.skipped.length > 0) notice.push(t("chat.formFill.skipped", { count: result.skipped.length }));
      setFormFillNotice(notice.join(" "));
      setFormFillRows([]);
    } catch (error) {
      setFormFillError(
        error instanceof FormFillBridgeError && error.code === "timeout"
          ? t("chat.formFill.outdated")
          : t("chat.formFill.failed")
      );
    } finally {
      setFormFillBusy(false);
    }
  };

  const scopeSubtitle = (kindKey: string, auto: boolean) =>
    `${t(`chat.resource.${kindKey}`)} · ${auto ? t("chat.resource.autoSelected") : t("chat.resource.manualSelected")}`;

  const scopeDetails = (id: string, meta?: ResourceMeta) => {
    const rows = [{ label: t("chat.resource.id"), value: id }];
    const pct = formatProbabilityPct(meta?.probability);
    if (pct) rows.push({ label: t("chat.resource.confidence"), value: pct });
    return rows;
  };

  const composerBubbles: ChatScopeBubbleItem[] = [
    ...selectedIndices.map((idx): ChatScopeBubbleItem => {
      const auto = autoResourceKeys.has(resourceAutoKey("index", idx));
      const meta = resourceMeta[idx];
      return {
        key: `scope-idx-${idx}`,
        kind: "index",
        auto,
        title: meta?.name || friendlyIndexLabel(idx),
        subtitle: scopeSubtitle("kindIndex", auto),
        details: scopeDetails(idx, meta),
        actions: [
          {
            label: t("chat.resource.remove"),
            color: "error",
            onClick: () => handleSelectedIndicesChange(selectedIndices.filter((x) => x !== idx)),
          },
        ],
      };
    }),
    ...selectedAdaptors.map((a): ChatScopeBubbleItem => {
      const auto = autoResourceKeys.has(resourceAutoKey("adaptor", a.id));
      const meta = resourceMeta[a.id];
      return {
        key: `scope-adp-${a.id}`,
        kind: "adaptor",
        auto,
        title: meta?.name || (a.type ? `${a.name} (${a.type})` : a.name),
        subtitle: scopeSubtitle("kindAdaptor", auto),
        details: scopeDetails(a.id, meta),
        actions: [
          {
            label: t("chat.resource.remove"),
            color: "error",
            onClick: () => handleSelectedAdaptorsChange(selectedAdaptors.filter((x) => x.id !== a.id)),
          },
        ],
      };
    }),
    ...(openApiSelection
      ? [
          ((): ChatScopeBubbleItem => {
            const auto = autoResourceKeys.has(resourceAutoKey("openapi", openApiSelection.catalogId));
            const meta = resourceMeta[openApiSelection.catalogId];
            return {
              key: "scope-openapi",
              kind: "openapi",
              auto,
              title: openApiSelection.tag
                ? `${openApiSelection.catalogName} / ${openApiSelection.tag}`
                : meta?.name || openApiSelection.catalogName,
              subtitle: scopeSubtitle("kindOpenapi", auto),
              details: scopeDetails(openApiSelection.catalogId, meta),
              actions: [
                { label: t("chat.resource.remove"), color: "error", onClick: () => handleOpenApiSelectionChange(null) },
              ],
            };
          })(),
        ]
      : []),
    ...(outputPreference
      ? [
          {
            key: "scope-output",
            kind: "output",
            auto: outputPreferencePredicted,
            title: outputPreferenceLabel(outputPreference, t, i18n.language),
            subtitle: outputPreferencePredicted
              ? `${t("chat.resource.kindOutput")} · ${t("chat.slash.predicted")}`
              : scopeSubtitle("kindOutput", false),
            actions: [
              {
                label: t("chat.resource.remove"),
                color: "error",
                onClick: () => {
                  setOutputPreference(null);
                  setOutputPreferenceLocked(false);
                  setOutputPreferencePredicted(false);
                },
              },
            ],
          } satisfies ChatScopeBubbleItem,
        ]
      : []),
    ...resourceCandidates
      .filter((cand) => {
        if (!cand.id) return false;
        if (cand.kind === "index" && selectedIndices.includes(cand.id)) return false;
        if (cand.kind === "adaptor" && selectedAdaptors.some((a) => a.id === cand.id)) return false;
        if (cand.kind === "openapi" && openApiSelection?.catalogId === cand.id) return false;
        return true;
      })
      .slice(0, 4)
      .map((cand): ChatScopeBubbleItem => {
        const id = cand.id || "";
        return {
          key: `cand-${cand.kind}-${id}`,
          kind: "candidate",
          title: cand.name || friendlyIndexLabel(id),
          subtitle: `${t("chat.resource.suggestion")} · ${t(
            cand.kind === "adaptor" ? "chat.resource.kindAdaptor" : cand.kind === "openapi" ? "chat.resource.kindOpenapi" : "chat.resource.kindIndex"
          )}`,
          details: scopeDetails(id, { name: cand.name || id, probability: cand.probability }),
          actions: [
            {
              label: t("chat.resource.add"),
              variant: "contained",
              onClick: () => {
                if (cand.kind === "index") {
                  handleSelectedIndicesChange(selectedIndices.includes(id) ? selectedIndices : [...selectedIndices, id]);
                } else if (cand.kind === "adaptor") {
                  handleSelectedAdaptorsChange(
                    selectedAdaptors.some((a) => a.id === id)
                      ? selectedAdaptors
                      : [...selectedAdaptors, { id, name: cand.name || id, type: "" }]
                  );
                } else if (cand.kind === "openapi") {
                  handleOpenApiSelectionChange({ catalogId: id, catalogName: cand.name || id });
                }
              },
            },
          ],
        };
      }),
    ...(suggestions.length > 0
      ? [
          {
            key: "suggestions",
            kind: "suggestions",
            count: suggestions.length,
            title: t("chat.suggest.title"),
            content: (close: () => void) => (
              <List dense disablePadding sx={{ maxHeight: 260, overflowY: "auto" }}>
                {suggestions.map((sug) => (
                  <ListItemButton
                    key={`suggest-${sug}`}
                    onClick={() => {
                      setInput(sug);
                      close();
                    }}
                    sx={{ borderRadius: "10px" }}
                  >
                    <ListItemText primary={sug} primaryTypographyProps={{ variant: "body2" }} />
                  </ListItemButton>
                ))}
              </List>
            ),
            actions: [{ label: t("chat.suggest.dismiss"), variant: "text", onClick: () => setSuggestions([]) }],
          } satisfies ChatScopeBubbleItem,
        ]
      : []),
  ];

  const messageScopeBubbles = (msg: ChatMessage): ChatScopeBubbleItem[] => {
    const scope = msg.messageScope;
    if (!scope) return [];
    const items: ChatScopeBubbleItem[] = [];
    scope.indices?.forEach((idx) => {
      const auto = isAutoOnMessageScope(msg, "index", idx);
      const meta: ResourceMeta = {
        name: scope.indexLabels?.[idx] || resourceMeta[idx]?.name || friendlyIndexLabel(idx),
        probability: resourceMeta[idx]?.probability,
      };
      items.push({
        key: `idx-${idx}`,
        kind: "index",
        auto,
        title: meta.name || idx,
        subtitle: scopeSubtitle("kindIndex", auto),
        details: scopeDetails(idx, meta),
      });
    });
    if (scope.openApi) {
      const catalogId = scope.openApi.catalogId || "";
      const auto = isAutoOnMessageScope(msg, "openapi", catalogId);
      items.push({
        key: "openapi-scope",
        kind: "openapi",
        auto,
        title: openApiScopeLabel(scope.openApi),
        subtitle: scopeSubtitle("kindOpenapi", auto),
        details: catalogId ? scopeDetails(catalogId) : undefined,
      });
    }
    scope.adaptors?.forEach((a) => {
      const auto = isAutoOnMessageScope(msg, "adaptor", a.id);
      items.push({
        key: `adp-${a.id}`,
        kind: "adaptor",
        auto,
        title: a.type ? `${a.name || a.id} (${a.type})` : a.name || a.id,
        subtitle: scopeSubtitle("kindAdaptor", auto),
        details: scopeDetails(a.id),
      });
    });
    return items;
  };

  return (
    <Box
      sx={chat.root({ isPageFullscreen, darkMode, c })}
    >
      {!host.compact && (
      <Tooltip title={isPageFullscreen ? t("chat.exitFullscreen") : t("chat.expandFullscreen")} arrow>
        <AppIconButton
          aria-label={isPageFullscreen ? t("chat.exitFullscreenAria") : t("chat.expandFullscreenAria")}
          size="small"
          onClick={() => {
            togglePageFullscreen();
          }}
          sx={chat.fullscreenToggle({ c, darkMode })}
        >
          {isPageFullscreen ? (
            <FullscreenExitRoundedIcon sx={chat.iconSize22} />
          ) : (
            <FullscreenRoundedIcon sx={chat.iconSize22} />
          )}
        </AppIconButton>
      </Tooltip>
      )}
      {/* Left rail — Gemini: history + new chat (can hide entirely or widen when open) */}
      {features.showHistory && (
      <Box
        ref={historyRailRef}
        sx={[
          chat.historyRail({ c, historyRailOpen, historyRailExpanded }),
          host.compact && historyRailOpen && compactRailOverlaySx,
        ]}
      >
        <Box sx={chat.historyRailHeader}>
          <AppButton
            fullWidth
            onClick={startNewChat}
            startIcon={<AddIcon sx={chat.iconSize20} />}
            variant="outlined"
            color="inherit"
            sx={chat.newChatButton(c)}
          >
            {t("chat.newChat")}
          </AppButton>
          <Box sx={chat.historySectionHeader}>
            <Typography variant="caption" sx={chat.historyMutedCaption(c)}>
              {t("chat.recent")}
            </Typography>
            <Box sx={chat.historyItemTitleRow}>
              <Tooltip title={t("chat.hideHistory")} arrow>
                <AppIconButton
                  size="small"
                  onClick={() => setHistoryRailOpen(false)}
                  sx={chat.historyIconMuted(c)}
                  aria-label={t("chat.hideHistory")}
                >
                  <KeyboardDoubleArrowLeftRoundedIcon sx={chat.iconSize22} />
                </AppIconButton>
              </Tooltip>
              <ChatHistoryMoreMenu
                historyRailExpanded={historyRailExpanded}
                showHistoryFilters={showHistoryFilters}
                onToggleExpand={() => setHistoryRailExpanded((v) => !v)}
                onToggleFilters={() => setShowHistoryFilters((v) => !v)}
                iconSx={chat.historyIconMuted(c)}
                t={t}
              />
            </Box>
          </Box>
          <Collapse in={showHistoryFilters} timeout="auto" unmountOnExit>
            <Box sx={chat.historyFilterBox}>
              <AppTextField
                size="small"
                placeholder={t("chat.searchHistory")}
                value={historyTitleQuery}
                onChange={(e) => setHistoryTitleQuery(e.target.value)}
                sx={chat.historySearchField(c)}
              />
              <Box sx={chat.historyDateRow}>
                <AppTextField
                  size="small"
                  type="datetime-local"
                  label={t("dateRange.from")}
                  value={historyDateFrom}
                  onChange={(e) => setHistoryDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={chat.historyDateField(c)}
                />
                <AppTextField
                  size="small"
                  type="datetime-local"
                  label={t("dateRange.to")}
                  value={historyDateTo}
                  onChange={(e) => setHistoryDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={chat.historyDateField(c)}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>
        <Box sx={chat.historyListScroll}>
          {historyLoading ? (
            <Box sx={chat.historyLoadingRow}>
              <CircularProgress size={18} sx={chat.historyLoadingSpinner(c)} />
              <Typography variant="body2" sx={chat.historyLoadingText(c)}>
                {t("common.loading")}
              </Typography>
            </Box>
          ) : filteredHistory.length === 0 ? (
            <Box sx={chat.historyEmptyBox}>
              <Typography variant="body2" sx={chat.historyEmptyText(c)}>
                {t("chat.historyNoMatch")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {visibleHistory.map((h) => {
                const running =
                  historyItemIsRunning(h) || (h.id === selectedHistoryId && openThreadAwaiting);
                return (
                <React.Fragment key={h.id}>
                  <ListItemButton
                    selected={selectedHistoryId === h.id}
                    onClick={() => {
                      void selectHistory(h);
                      if (host.compact) setHistoryRailOpen(false);
                    }}
                    sx={chat.historyItem(c)}
                  >
                    <Box sx={chat.historyItemInner}>
                      <Box sx={chat.historyItemTitleRow}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                          {running && (
                            <Tooltip title={t("chat.historyRunning")} arrow>
                              <Box component="span" sx={chat.historyRunningIcon} aria-label={t("chat.historyRunning")}>
                                <CircularProgress size={16} sx={chat.historyLoadingSpinner(c)} />
                              </Box>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={[h.title || t("chat.untitled"), h.username].filter(Boolean).join(" · ") || t("chat.historyTitle")}
                            arrow
                          >
                            <Typography
                              variant="body2"
                              sx={chat.historyItemTitle({ c, historyRailExpanded })}
                            >
                              {truncateTitle(h.title, historyRailExpanded ? 36 : 22)}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Tooltip title={t("common.delete")} arrow>
                          <AppIconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteHistory(h.id);
                            }}
                            sx={chat.historyItemDelete(c)}
                          >
                            <Icon fontSize="small">close</Icon>
                          </AppIconButton>
                        </Tooltip>
                      </Box>
                      {h.username ? (
                        <Typography variant="caption" sx={chat.historyItemUsername(c)}>
                          {h.username}
                        </Typography>
                      ) : null}
                      {h.createdDate ? (
                        <Typography variant="caption" sx={chat.historyItemDate(c)}>
                          {formatDateTime(h.createdDate)}
                        </Typography>
                      ) : null}
                    </Box>
                  </ListItemButton>
                </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      )}

      {/* Main canvas — centered thread + floating composer */}
      <Box
        sx={{ ...chat.mainCanvas(c), position: "relative" }}
      >
        {chatRuns.length > 0 && (
          <Stack
            spacing={1}
            sx={{
              position: "absolute",
              top: 56,
              right: 12,
              zIndex: 8,
              alignItems: "flex-end",
            }}
          >
            {chatRuns.map((job) => (
              <ChatRunChip
                key={job.turnId}
                job={job}
                onChange={(next) => {
                  if (isTerminalChatTurnStatus(next.status)) {
                    setChatRuns((prev) => prev.filter((item) => item.turnId !== next.turnId));
                    return;
                  }
                  setChatRuns((prev) => prev.map((item) => (item.turnId === next.turnId ? next : item)));
                  setMessages((prev) =>
                    applyProgressToMessages(prev, {
                      step: next.step,
                      tool: next.tool,
                      detail: next.timeline?.at(-1)?.detail,
                      timeline: next.timeline,
                      messageId: next.messageId,
                    })
                  );
                }}
                onTerminal={(next) => {
                  setChatRuns((prev) => prev.filter((item) => item.turnId !== next.turnId));
                  if (resumeWatchRef.current && selectedHistoryIdRef.current) {
                    void absorbFinishedTurn(selectedHistoryIdRef.current);
                  }
                }}
              />
            ))}
          </Stack>
        )}
        {(!historyRailOpen && features.showHistory) || !features.showHistory ? (
          <Box sx={chat.canvasActionRow}>
            {!historyRailOpen && features.showHistory && (
              <Tooltip title={t("chat.showHistory")} arrow>
                <AppIconButton
                  busyIgnore
                  onClick={() => setHistoryRailOpen(true)}
                  aria-label={t("chat.showHistory")}
                  sx={chat.canvasIconButton({ c, darkMode })}
                >
                  <HistoryRoundedIcon sx={chat.iconSize26} />
                </AppIconButton>
              </Tooltip>
            )}
            <Tooltip title={t("chat.newChat")} arrow>
              <AppIconButton
                busyIgnore
                onClick={startNewChat}
                aria-label={t("chat.newChat")}
                sx={chat.canvasIconButton({ c, darkMode })}
              >
                <AddIcon sx={chat.iconSize26} />
              </AppIconButton>
            </Tooltip>
          </Box>
        ) : null}
        <Box
          sx={chat.threadScroll}
        >
          <Box
            sx={chat.threadInner(historyRailOpen)}
          >
            {packageLimits && !packageLimits.unlimited && packageLimits.aiAnalysisMaxChars != null && (
              <PackageLimitHint group="llm" capped={packageLimits.aiAnalysisMaxChars != null}>
                {t("packageLimits.aiAnalysisChars", {
                  chars: formatLimitValue(
                    packageLimits.aiAnalysisMaxChars,
                    t("packageLimits.unlimited")
                  ),
                })}
              </PackageLimitHint>
            )}
            {historyConversationLoading && (
              <Box
                sx={chat.conversationLoading}
              >
                <CircularProgress sx={chat.conversationLoadingSpinner(c)} />
              </Box>
            )}
            {!historyConversationLoading && messages.length === 0 && (
              <Box
                sx={chat.emptyState}
              >
                <Box
                  component="img"
                  src={brandMark}
                  alt=""
                  sx={chat.emptyStateIcon(darkMode)}
                />
                <Typography
                  variant="h4"
                  sx={chat.emptyStateGreeting(c)}
                >
                  {t("chat.greeting")}
                </Typography>
                <Typography variant="body1" sx={chat.emptyStateSubtitle(c)}>
                  {host.welcomeMessage || t("chat.greetingSubtitle")}
                </Typography>
              </Box>
            )}

            {!historyConversationLoading &&
              messages.map((msg) => {
              const isWideResponse =
                msg.role === "assistant" &&
                !!msg.payload &&
                ["chart", "table", "json", "html"].includes(String(msg.payload.type || "").toLowerCase());
              const copyText = copyableChatText(msg);
              const showAssistantTools =
                (features.mutations && canEmailMessage(msg)) ||
                (canUseRagPromote && canSaveAsExample(msg)) ||
                (features.export &&
                  msg.payload &&
                  ["chart", "table", "json", "html"].includes(String(msg.payload.type || "").toLowerCase()));
              return (
                <Box
                  key={msg.id}
                  sx={chat.messageRow(msg.role === "user")}
                >
                  <Box
                    sx={chat.messageBubbleWrap(isWideResponse)}
                  >
                    {msg.role === "user" ? (
                      <Box
                        sx={chat.userMessageColumn}
                      >
                        {(msg.displayUsername || msg.timestamp) && (
                          <Typography
                            variant="caption"
                            component="div"
                            sx={chat.userMetaCaption(c)}
                          >
                            {[msg.displayUsername, formatChatHistoryDate(msg.timestamp)].filter(Boolean).join(" · ")}
                          </Typography>
                        )}
                        <Box
                          sx={chat.userMessageRow}
                        >
                          {hasChatMessageScope(msg.messageScope) && (
                            <Box sx={chat.userScopeRow}>
                              <ChatScopeBubbles items={messageScopeBubbles(msg)} size="small" />
                            </Box>
                          )}
                          {copyText && (
                            <ChatCopyButton text={copyText} sx={{ ...chat.historyIconMuted(c), alignSelf: "flex-end" }} />
                          )}
                          <Paper
                            elevation={0}
                            sx={chat.userBubble(c)}
                          >
                            <Typography
                              component="div"
                              sx={chat.userBubbleText}
                            >
                              {msg.content}
                            </Typography>
                          </Paper>
                          <Box
                            sx={chat.userAvatarCircle(darkMode)}
                          >
                            <Box
                              component="img"
                              src={userAvatarIcon}
                              alt=""
                              sx={chat.userAvatarImage(darkMode)}
                            />
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={chat.assistantRow}>
                        <Box sx={chat.assistantRail}>
                          <Box
                            sx={chat.assistantAvatarCircle(darkMode)}
                          >
                            <Box
                              component="img"
                              src={brandMark}
                              alt=""
                              sx={chat.assistantAvatarImage}
                            />
                          </Box>
                          {(copyText || showAssistantTools) && (
                            <Box sx={chat.assistantActions}>
                              {copyText && (
                                <ChatCopyButton text={copyText} sx={chat.historyIconMuted(c)} />
                              )}
                              {features.mutations && canEmailMessage(msg) && (
                                <Tooltip title={t("chat.mail.action")} arrow>
                                  <AppIconButton
                                    size="small"
                                    onClick={() => void openMailDialogForMessage(msg)}
                                    sx={chat.historyIconMuted(c)}
                                  >
                                    <EmailRoundedIcon fontSize="small" />
                                  </AppIconButton>
                                </Tooltip>
                              )}
                              {canUseRagPromote && canSaveAsExample(msg) && (
                                (() => {
                                  const hasQueries = extractExampleQueries(msg).length > 0;
                                  const saved = !!msg.payload?.messageId && savedExampleMessageIds.has(msg.payload.messageId);
                                  return (
                                    <Tooltip
                                      title={
                                        saved
                                          ? t("chat.rag.saved")
                                          : hasQueries
                                            ? t("chat.rag.action")
                                            : t("chat.rag.noQuery")
                                      }
                                      arrow
                                    >
                                      <span>
                                        <AppIconButton
                                          size="small"
                                          disabled={!hasQueries || saved}
                                          onClick={() => openRagDialogForMessage(msg)}
                                          sx={chat.historyIconMuted(c)}
                                        >
                                          {saved ? (
                                            <LibraryAddCheckRoundedIcon fontSize="small" />
                                          ) : (
                                            <LibraryAddRoundedIcon fontSize="small" />
                                          )}
                                        </AppIconButton>
                                      </span>
                                    </Tooltip>
                                  );
                                })()
                              )}
                              {features.export &&
                                msg.payload &&
                                ["chart", "table", "json", "html"].includes(
                                  String(msg.payload.type || "").toLowerCase()
                                ) && (
                                  <>
                                    <Tooltip title={t("export.print")} arrow>
                                      <AppIconButton
                                        size="small"
                                        onClick={() =>
                                          handlePrintPayload(msg.payload as ChatOrchestrationWebSocketPayload)
                                        }
                                        sx={chat.historyIconMuted(c)}
                                      >
                                        <Icon fontSize="small">print</Icon>
                                      </AppIconButton>
                                    </Tooltip>
                                    <Tooltip title={t("chat.fullscreen")} arrow>
                                      <AppIconButton
                                        size="small"
                                        onClick={() => setFullscreenPayload(msg.payload || null)}
                                        sx={chat.historyIconMuted(c)}
                                      >
                                        <Icon fontSize="small">open_in_full</Icon>
                                      </AppIconButton>
                                    </Tooltip>
                                  </>
                                )}
                            </Box>
                          )}
                        </Box>
                        <Box sx={chat.assistantContent}>
                          <Box sx={chat.assistantHeader}>
                            <Typography variant="caption" sx={chat.assistantLabel(c)}>
                              {t("chat.assistantLabel")}
                            </Typography>
                          </Box>
                          {msg.role === "assistant" &&
                            (msg.historyStatus === "COMPLETED" ||
                              (msg.historyStatus == null &&
                                msg.payload &&
                                !msg.error &&
                                String(msg.id).startsWith("h-assistant"))) && (
                            <Typography
                              variant="caption"
                              component="div"
                              sx={chat.assistantMetaCaption(c)}
                            >
                              {formatChatHistoryDate(msg.historyCreatedAt || msg.timestamp)}
                              {` · ${resolveAssistantResponseTypeLabel(msg, t)}`}
                            </Typography>
                          )}
                          {msg.role === "assistant" && msg.historyStatus === "ERROR" && (
                            <Typography
                              variant="caption"
                              component="div"
                              sx={chat.assistantErrorCaption}
                            >
                              {(() => {
                                const when = formatChatHistoryDate(msg.historyCreatedAt || msg.timestamp);
                                const rt = msg.historyResponseType
                                  ? ` · ${String(msg.historyResponseType).toLowerCase()}`
                                  : "";
                                return `${t("chat.systemError")}${when ? ` · ${when}` : ""}${rt}`;
                              })()}
                            </Typography>
                          )}
                          {msg.error && (
                            <Typography variant="body2" sx={chat.assistantErrorText}>
                              {msg.error}
                            </Typography>
                          )}
                          {msg.payload && (
                            <Box sx={chat.assistantPayload(c)}>
                              <ResponseRenderer
                                payload={msg.payload}
                                clarificationComposerText={input}
                                clarificationOriginalRequest={findOriginalUserRequest(messages)}
                                onClarificationAnswer={(answer) =>
                                  void submitMessage(answer, undefined, { preserveOutputPreference: true })
                                }
                                onWidgetDraftPersisted={(next) =>
                                  handleWidgetDraftPersisted(msg.id, next)
                                }
                              />
                              {!features.warehouseJobs && asyncWarehouseJobs(msg).length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  {t("chat.embed.featureDisabled")}
                                </Typography>
                              )}
                              {features.warehouseJobs && asyncWarehouseJobs(msg).map((q) => (
                                <WarehouseJobCard
                                  key={q.jobId}
                                  jobId={q.jobId as string}
                                  correlationId={msg.payload?.correlationId || correlationIdRef.current || undefined}
                                  adaptorName={q.adaptorName}
                                  sql={typeof q.query === "string" ? q.query : undefined}
                                />
                              ))}
                            </Box>
                          )}
                          {!msg.payload && !msg.error && (
                            <Box
                              sx={{
                                ...chat.assistantThinkingRow,
                                ...(msg.puffing
                                  ? {
                                      animation: "chatThinkPuff 0.45s ease forwards",
                                      "@keyframes chatThinkPuff": {
                                        to: { opacity: 0, transform: "scale(0.55) translate(48%, -80%)" },
                                      },
                                    }
                                  : {}),
                              }}
                            >
                              <Box
                                aria-hidden
                                sx={chat.assistantThinkingBlocks(c)}
                              >
                                <Box sx={chat.assistantThinkingBlock(c)} />
                                <Box sx={chat.assistantThinkingBlock(c)} />
                                <Box sx={chat.assistantThinkingBlock(c)} />
                                <Box sx={chat.assistantThinkingBlock(c)} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {(msg.progressTimeline?.length || 0) > 1 ? (
                                  <ChatProgressSteps
                                    c={c}
                                    current={formatProgressLabel(
                                      t,
                                      msg.progressStep,
                                      msg.progressTool,
                                      msg.progressDetail
                                    )}
                                    textSx={chat.assistantThinkingText(c)}
                                    label={t("chat.progress.timeline")}
                                    steps={(msg.progressTimeline || []).map((entry) =>
                                      formatProgressLabel(t, entry.step, entry.tool, entry.detail)
                                    )}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={chat.assistantThinkingText(c)}>
                                    {formatProgressLabel(
                                      t,
                                      msg.progressStep,
                                      msg.progressTool,
                                      msg.progressDetail
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
            <Box ref={messagesEndRef} />
          </Box>
        </Box>

        {/* Gemini-style pill composer */}
        <Box
          sx={chat.composerFooter(c)}
        >
          <Box sx={chat.composerInner}>
            {composerBubbles.length > 0 && (
              <Box sx={chat.composerIndexRow}>
                <ChatScopeBubbles items={composerBubbles} />
              </Box>
            )}
            <Paper
              elevation={0}
              sx={{ ...chat.composerPaper({ c, darkMode }), position: "relative" }}
            >
              <ChatSlashCommandPicker
                open={slashOpen}
                mode={slashMode}
                query={slashQuery}
                forOutputType={slashChartFor ?? undefined}
                onSelectCommand={handleSelectSlashCommand}
                onSelectWidgetFormat={handleSelectWidgetFormat}
                onSelectChartType={handleSelectChartType}
              />
              <ChatToolsPalette
                disabled={loading}
                iconColor={c.muted}
                activeCount={
                  selectedIndices.length + selectedAdaptors.length + (openApiSelection ? 1 : 0) + (outputPreference ? 1 : 0)
                }
                items={[
                  ...(features.scopePickers
                    ? [
                        {
                          key: "indices",
                          icon: <ManageSearchRoundedIcon fontSize="small" />,
                          label: t("chat.tools.indices"),
                          hint: t("chat.tooltips.indices"),
                          active: selectedIndices.length > 0,
                          onSelect: () => setToolPicker("index"),
                        },
                        {
                          key: "adaptors",
                          icon: <StorageRoundedIcon fontSize="small" />,
                          label: t("chat.tools.adaptors"),
                          hint: t("chat.tooltips.adaptors"),
                          active: selectedAdaptors.length > 0,
                          onSelect: () => setToolPicker("adaptor"),
                        },
                        {
                          key: "openapi",
                          icon: <HubRoundedIcon fontSize="small" />,
                          label: t("chat.tools.openapi"),
                          hint: t("chat.tooltips.openapi"),
                          active: Boolean(openApiSelection),
                          onSelect: () => setToolPicker("openapi"),
                        },
                      ]
                    : []),
                  {
                    key: "slash",
                    icon: <Box component="span" sx={{ fontSize: "1.05rem", lineHeight: 1 }}>/</Box>,
                    label: t("chat.tools.slash"),
                    hint: t("chat.tooltips.slash"),
                    active: Boolean(outputPreference),
                    onSelect: () => {
                      if (!input.trim()) {
                        handleComposerInputChange("/");
                        return;
                      }
                      setSlashMode("commands");
                      setSlashChartFor(null);
                      setSlashWidgetFormat(null);
                      setSlashQuery("");
                      setSlashOpen(true);
                    },
                  },
                  {
                    key: "suggest",
                    icon: <AutoAwesomeRoundedIcon fontSize="small" />,
                    label: t("chat.tools.suggest"),
                    hint: t("chat.tooltips.suggest"),
                    busy: suggestLoading,
                    active: suggestions.length > 0,
                    onSelect: () => void handleSuggestPrompts(),
                  },
                ]}
              >
                {features.scopePickers && (
                  <Box sx={{ position: "absolute", left: 0, bottom: 0, width: 0, height: 0 }}>
                    <ChatIndexPicker
                      hideTrigger
                      open={toolPicker === "index"}
                      onOpenChange={(o) => setToolPicker(o ? "index" : null)}
                      selected={selectedIndices}
                      onChange={handleSelectedIndicesChange}
                      disabled={loading}
                      labels={resourceMeta}
                    />
                    <ChatAdaptorPicker
                      hideTrigger
                      open={toolPicker === "adaptor"}
                      onOpenChange={(o) => setToolPicker(o ? "adaptor" : null)}
                      selected={selectedAdaptors}
                      onChange={handleSelectedAdaptorsChange}
                      disabled={loading}
                    />
                    <ChatOpenApiPicker
                      hideTrigger
                      open={toolPicker === "openapi"}
                      onOpenChange={(o) => setToolPicker(o ? "openapi" : null)}
                      value={openApiSelection}
                      onChange={handleOpenApiSelectionChange}
                      disabled={loading}
                    />
                  </Box>
                )}
              </ChatToolsPalette>
              <AppTextField
                multiline
                maxRows={6}
                placeholder={t("chat.promptPlaceholder")}
                value={input}
                onChange={(e) => handleComposerInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                variant="standard"
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  sx: chat.composerInputProps(c),
                }}
                sx={chat.composerInputPlaceholder(c)}
              />
              {isFormFillEnabled(host) && (
                <Tooltip title={t("chat.formFill.action")} arrow>
                  <Box component="span" sx={chat.composerMicWrapper}>
                    <AppIconButton
                      size="small"
                      onClick={() => void handleFormFill()}
                      disabled={!formFillAnswer || formFillBusy}
                      aria-label={t("chat.formFill.action")}
                      sx={chat.historyIconMuted(c)}
                    >
                      {formFillBusy && !formFillOpen ? (
                        <CircularProgress size={16} sx={{ color: c.muted }} />
                      ) : (
                        <EditNoteRoundedIcon fontSize="small" />
                      )}
                    </AppIconButton>
                  </Box>
                </Tooltip>
              )}
              <Tooltip
                title={
                  isListening ? t("chat.stopListening") : t("chat.tooltips.microphone")
                }
                arrow
              >
                <Box component="span" sx={chat.composerMicWrapper}>
                  <AppIconButton
                    size="small"
                    onClick={toggleSpeechToText}
                    disabled={loading}
                    sx={chat.composerMicButton({ isListening, c })}
                    aria-label={isListening ? t("chat.stopListening") : t("chat.tooltips.microphone")}
                  >
                    {isListening ? <StopRoundedIcon fontSize="small" /> : <MicRoundedIcon fontSize="small" />}
                  </AppIconButton>
                </Box>
              </Tooltip>
              <AppIconButton
                onClick={handleSend}
                disabled={!input.trim() || loading}
                sx={chat.composerSendButton({ c })}
                aria-label={t("chat.send", { defaultValue: "Send" })}
              >
                <NorthRoundedIcon fontSize="small" />
              </AppIconButton>
            </Paper>
            <Typography variant="caption" sx={chat.composerDisclaimer(c)}>
              {t("chat.disclaimer")}
            </Typography>
          </Box>
        </Box>
      </Box>

      <FormFillDialog
        open={formFillOpen}
        busy={formFillBusy}
        error={formFillError}
        notice={formFillNotice}
        rows={formFillRows}
        onChange={(index, value) =>
          setFormFillRows((prev) => prev.map((row, i) => (i === index ? { ...row, value } : row)))
        }
        onClose={() => {
          if (formFillBusy) return;
          setFormFillOpen(false);
        }}
        onConfirm={() => void confirmFormFill()}
      />

      <AppDialog open={pageConsentOpen} onClose={() => !pageConsentBusy && handlePageContentConsentReject()} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t("chat.pageContent.consentTitle")}</AppDialogTitle>
        <AppDialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("chat.pageContent.consentBody")}
          </Typography>
          {pageConsentBusy && (
            <Stack direction="row" spacing={1} alignItems="center" mt={2}>
              <CircularProgress size={18} />
              <Typography variant="body2">{t("chat.pageContent.busy")}</Typography>
            </Stack>
          )}
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={handlePageContentConsentReject} disabled={pageConsentBusy}>
            {t("chat.pageContent.reject")}
          </AppDialogCancelButton>
          <AppDialogPrimaryButton onClick={() => void handlePageContentConsentApprove()} disabled={pageConsentBusy}>
            {t("chat.pageContent.approve")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>

      <AppDialog
        fullScreen
        open={fullscreenPayload != null}
        onClose={() => setFullscreenPayload(null)}
        slotProps={{
          paper: {
            sx: chat.fullscreenDialogPaper(c),
          },
        }}
      >
        <AppDialogTitle
          endAdornment={
            <AppIconButton onClick={() => setFullscreenPayload(null)} sx={chat.fullscreenDialogClose(c)} aria-label={t("common.close")}>
              <Icon>close_fullscreen</Icon>
            </AppIconButton>
          }
        >
          {t("chat.fullscreenTitle")}
        </AppDialogTitle>
        <AppDialogContent sx={chat.fullscreenDialogContent}>
          {fullscreenPayload && <ResponseRenderer payload={fullscreenPayload} />}
        </AppDialogContent>
      </AppDialog>

      <AppDialog
        open={mailDialogOpen}
        onClose={() => !mailSubmitting && !mailPreparing && setMailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <AppDialogTitle>{t("chat.mail.dialogTitle")}</AppDialogTitle>
        <AppDialogContent>
          <AppFormGrid>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("chat.mail.to")}
                value={mailTo}
                onChange={(e) => setMailTo(e.target.value)}
                placeholder={t("chat.mail.toPlaceholder")}
                helperText={t("chat.mail.toHint")}
                disabled={mailSubmitting || mailPreparing}
                autoFocus
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("chat.mail.subject")}
                value={mailSubject}
                onChange={(e) => setMailSubject(e.target.value)}
                disabled={mailSubmitting || mailPreparing}
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("chat.mail.bodyPreviewHint")}
              </Typography>
              {mailPreparing ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : mailHtml ? (
                <Box
                  component="iframe"
                  title={t("chat.mail.bodyPreview")}
                  srcDoc={mailBody}
                  sandbox="allow-same-origin"
                  sx={{
                    width: "100%",
                    minHeight: 320,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                />
              ) : (
                <AppTextField
                  label={t("chat.mail.body")}
                  value={mailBody}
                  onChange={(e) => setMailBody(e.target.value)}
                  multiline
                  minRows={8}
                  disabled={mailSubmitting}
                />
              )}
            </AppFormField>
          </AppFormGrid>
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton
            onClick={() => setMailDialogOpen(false)}
            disabled={mailSubmitting || mailPreparing}
          >
            {t("common.cancel")}
          </AppDialogCancelButton>
          <AppDialogPrimaryButton
            onClick={() => void submitMailDraft()}
            disabled={mailSubmitting || mailPreparing || !mailBody.trim()}
          >
            {mailSubmitting ? t("chat.mail.creating") : t("chat.mail.createDraft")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>

      <AppDialog
        open={ragDialogOpen}
        onClose={() => !ragSubmitting && setRagDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <AppDialogTitle>{t("chat.rag.dialogTitle")}</AppDialogTitle>
        <AppDialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("chat.rag.dialogHint")}
          </Typography>
          {ragError ? (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {ragError}
            </Typography>
          ) : null}
          <AppFormGrid>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("chat.rag.question")}
                value={ragQuestion}
                onChange={(e) => setRagQuestion(e.target.value)}
                multiline
                minRows={2}
                disabled={ragSubmitting}
                autoFocus
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("chat.rag.summary")}
                value={ragSummary}
                onChange={(e) => setRagSummary(e.target.value)}
                multiline
                minRows={2}
                disabled={ragSubmitting}
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t("chat.rag.queries")}
              </Typography>
              <Stack spacing={1}>
                {ragQueries.map((q, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!ragSelected[i]}
                            disabled={ragSubmitting}
                            onChange={(e) =>
                              setRagSelected((prev) => prev.map((v, idx) => (idx === i ? e.target.checked : v)))
                            }
                          />
                        }
                        label={t("chat.rag.include")}
                      />
                      <Chip size="small" color="primary" variant="outlined" label={t(`chat.rag.kind.${q.kind}`)} />
                      <Chip size="small" variant="outlined" label={q.tool || (q.kind === "opensearch" ? "OpenSearch" : "Adaptor_Query")} />
                      {q.kind === "opensearch" ? (
                        <Chip size="small" label={`${q.indexName}${q.searchType ? ` ${q.searchType}` : ""}`} />
                      ) : (
                        <Chip size="small" label={q.adaptorName || q.adaptorId} />
                      )}
                    </Stack>
                    <AppTextField
                      value={queryToText(q)}
                      multiline
                      minRows={3}
                      maxRows={10}
                      disabled
                      InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
                    />
                  </Paper>
                ))}
              </Stack>
            </AppFormField>
          </AppFormGrid>
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setRagDialogOpen(false)} disabled={ragSubmitting}>
            {t("common.cancel")}
          </AppDialogCancelButton>
          <AppDialogPrimaryButton
            onClick={() => void submitRagPromote()}
            disabled={ragSubmitting || !ragQuestion.trim() || !ragSelected.some(Boolean)}
          >
            {ragSubmitting ? t("chat.rag.promoting") : t("chat.rag.promote")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>
    </Box>
  );
}
